import React, { useState, useEffect, useRef } from "react";
import DashNavbar from "./DashNavbar";
import Sidebar from "./Sidebar";
import "../css/dash.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/PDFViewer.css";
import { Pagination, Spinner, Button, Modal, Card, Table, Form, Alert, InputGroup, Row, Col, Badge } from "react-bootstrap";
import axios from 'axios';
import Chart from "chart.js/auto";

// --- GLOBAL OPTIONS FOR MODAL ---
const jobStatusOptions = [
    { value: "applied", label: "Applied", bgColor: "bg-info" },
    { value: "under_review", label: "Under Review", bgColor: "bg-secondary" },
    { value: "interview_scheduled", label: "Interview Scheduled", bgColor: "bg-warning text-dark" },
    { value: "selected", label: "Selected", bgColor: "bg-success" },
    { value: "rejected", label: "Rejected", bgColor: "bg-danger" },
];

// --- HELPER FUNCTION TO FORMAT OPPORTUNITY TYPE ---
const formatOpportunityType = (type) => {
    if (!type) return 'N/A';
    switch (type) {
        case 'Business_Opportunity':
            return 'Business';
        case 'Technical_Consultant':
            return 'Consultant';
        case 'Job_Opening':
            return 'Job';
        default:
            return type;
    }
};

// Helper function for ApplicantDetailsModal
const renderField = (label, value) => {
    if (!value && value !== 0) return null;
    return (
        <Col md={6} className="mb-2">
            <div className="p-2 bg-light rounded">
                <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>
                    {label}
                </small>
                <span className="text-dark">{value}</span>
            </div>
        </Col>
    );
};

// --- APPLICANT DETAILS MODAL (Profile Data) ---
const ApplicantDetailsModal = ({ show, onHide, applicant }) => {
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (show && applicant) {
            setIsLoading(true);
            setTimeout(() => setIsLoading(false), 500);
        }
    }, [show, applicant]);

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="gradient-text">
                    <i className="fas fa-user-circle me-2"></i>
                    {applicant ? `${applicant.first_name || ''} ${applicant.last_name || ''}` : "Applicant Profile"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">Fetching profile details...</p>
                    </div>
                ) : applicant ? (
                    <div className="px-2">
                        {/* Personal Information */}
                        <Card className="shadow-sm mb-3 border-0">
                            <Card.Header className="bg-white fw-bold text-primary">Personal Details</Card.Header>
                            <Card.Body>
                                <Row>
                                    {renderField("Full Name", `${applicant.first_name || ''} ${applicant.middle_name || ''} ${applicant.last_name || ''}`)}
                                    {renderField("Email Address", applicant.email)}
                                    {renderField("Phone Number", applicant.phone)}
                                    {renderField("Gender", applicant.gender)}
                                    {renderField("Date of Birth", applicant.date_of_birth)}
                                    {renderField("Age", applicant.age)}
                                    {renderField("Marital Status", applicant.marital_status)}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Address Information */}
                        <Card className="shadow-sm mb-3 border-0">
                            <Card.Header className="bg-white fw-bold text-primary">Address & Location</Card.Header>
                            <Card.Body>
                                <Row>
                                    {renderField("Current Location", applicant.address_type)}
                                    {renderField("Village/City", applicant.village)}
                                    {renderField("Tehsil", applicant.tehsil)}
                                    {renderField("District", applicant.district)}
                                    {renderField("State", applicant.state)}
                                    {renderField("Country", applicant.country)}
                                    {renderField("Zip Code", applicant.zip_code)}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Professional & Education */}
                        <Card className="shadow-sm mb-3 border-0">
                            <Card.Header className="bg-white fw-bold text-primary">Education & Experience</Card.Header>
                            <Card.Body>
                                <Row>
                                    {renderField("Education Level", applicant.education)}
                                    {renderField("Degree", applicant.degree)}
                                    {renderField("Experience Type", <Badge bg={applicant.experience === 'Experienced' ? 'info' : 'secondary'}>{applicant.experience}</Badge>)}
                                    {applicant.experience === 'Experienced' && (
                                        <>
                                            {renderField("Total Experience", `${applicant.Work_total_experience_year || 0} Years ${applicant.Work_total_experience_months || 0} Months`)}
                                            {renderField("Current Company", applicant.current_comapny_name)}
                                            {renderField("Current Designation", applicant.job_title)}
                                            {renderField("Notice Period", `${applicant.current_notice_period_days} Days`)}
                                        </>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Financials (Only for Experienced) */}
                        {applicant.experience === 'Experienced' && (
                            <Card className="shadow-sm mb-3 border-0">
                                <Card.Header className="bg-white fw-bold text-primary">Financial Details & Reason for Leaving</Card.Header>
                                <Card.Body>
                                    <Row>
                                        {renderField("Current CTC", applicant.current_ctc)}
                                        {renderField("Gross Salary", applicant.gross)}
                                        {renderField("Net Salary", applicant.net)}
                                        {renderField("Variables", applicant.variables)}
                                        <Col md={12} className="mt-2">
                                            <div className="p-2 bg-light rounded">
                                                <small className="text-muted d-block fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>Reason for Leaving</small>
                                                <span className="text-dark">{applicant.reason_for_leaving || "No reason provided"}</span>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Referral */}
                        <Card className="shadow-sm mb-3 border-0">
                            <Card.Header className="bg-white fw-bold text-primary">Referral & Other</Card.Header>
                            <Card.Body>
                                <Row>
                                    {renderField("Referred By", applicant.referred_by)}
                                    {renderField("Referrer Contact", applicant.referred_contact)}
                                    {renderField("Acknowledged", applicant.acknowledged ? "Yes" : "No")}
                                    {renderField("Job Offered", applicant.job_offered ? <Badge bg="success">Yes</Badge> : <Badge bg="danger">No</Badge>)}
                                </Row>
                            </Card.Body>
                        </Card>
                    </div>
                ) : (
                    <div className="text-center p-4">No data found for this applicant.</div>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light">
                <Button variant="secondary" onClick={onHide}>Close Profile</Button>
            </Modal.Footer>
        </Modal>
    );
};

// --- SUB-MODAL FOR STANDALONE ANSWERS ---
const StandaloneAnswersModal = ({ show, onHide, applicant, jobId, openingPost, jobTitle, score }) => {
    const [jobQuestions, setJobQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (show && applicant?.id && jobId && openingPost) {
            fetchAnswers();
        }
    }, [show, applicant, jobId, openingPost]);

    const fetchAnswers = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(
                `https://raasbackend.vetrinahealthcare.com/questions/${openingPost}/${jobId}/?applicant_id=${applicant.id}`
            );
            const questionsData = response.data;
            if (questionsData && questionsData.questions) {
                setJobQuestions(questionsData.questions || []);
            } else if (Array.isArray(questionsData)) {
                setJobQuestions(questionsData);
            } else {
                setJobQuestions([]);
            }
        } catch (error) {
            console.error("Error fetching standalone answers:", error);
            setJobQuestions([]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable className="standalone-answers-modal">
            <Modal.Header closeButton className="bg-info text-white">
                <Modal.Title>
                    <i className="fas fa-file-alt me-2"></i>
                    Assessment Details: {jobTitle}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light p-4">
                <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-white rounded shadow-sm">
                    <div>
                        <h6 className="text-muted mb-1 text-uppercase small fw-bold">Candidate Score</h6>
                        <h3 className="mb-0 text-primary fw-bold">{score}%</h3>
                    </div>
                    <div className="text-end">
                        <h6 className="text-muted mb-1 text-uppercase small fw-bold">Job ID</h6>
                        <h5 className="mb-0 text-dark"> {jobId}</h5>
                    </div>
                </div>

                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">Retrieving detailed answers...</p>
                    </div>
                ) : jobQuestions.length > 0 ? (
                    <div className="row g-3">
                        {jobQuestions.map((question, index) => {
                            const userAnswer = question.user_selected_answer;
                            const isCorrect = userAnswer?.is_correct === "correct";
                            return (
                                <div key={question.id || index} className="col-12">
                                    <div className="bg-white rounded p-3 shadow-sm border-start border-4" style={{ borderColor: isCorrect ? '#28a745' : '#dc3545' }}>
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div className="d-flex align-items-start gap-2 flex-grow-1">
                                                <span className="badge bg-secondary flex-shrink-0" style={{ marginTop: '2px' }}>Q{index + 1}</span>
                                                <h6 className="fw-bold mb-0 text-start lh-base">
                                                    {question.question_text}
                                                </h6>
                                            </div>
                                            <Badge bg={isCorrect ? "success" : "danger"} className="rounded-pill flex-shrink-0 ms-2">
                                                {isCorrect ? "Correct" : "Incorrect"}
                                            </Badge>
                                        </div>
                                        <div className="options-stack small">
                                            {question.answers.map((opt, idx) => {
                                                const isSel = idx === userAnswer?.selected_index;
                                                const isCorr = idx === question.correct_answer_index;
                                                let variantClass = "bg-light";
                                                if (isSel && isCorr) variantClass = "bg-success text-white";
                                                else if (isSel) variantClass = "bg-danger text-white";
                                                else if (isCorr) variantClass = "bg-success bg-opacity-25 border border-success";

                                                return (
                                                    <div key={idx} className={`p-2 mb-1 rounded d-flex align-items-center ${variantClass}`}>
                                                        <span className="me-2 fw-bold text-uppercase">{String.fromCharCode(65 + idx)}.</span>
                                                        <span>{opt}</span>
                                                        {isCorr && <i className="fas fa-check-circle ms-auto px-2"></i>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center p-5 bg-white rounded shadow-sm">
                        <i className="fas fa-exclamation-circle text-warning fa-3x mb-3"></i>
                        <p className="text-muted">No detailed questions found for this assessment.</p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {/* <Button variant="outline-secondary" onClick={onHide}>Close Detailed View</Button> */}
            </Modal.Footer>
        </Modal>
    );
};


// --- APPLICANT RESULTS MODAL (Personality Assessments) ---
const ApplicantModal = ({ show, onHide, applicant, jobapplications, setSuccessMessage, updateJobApplication, primaryJobId }) => {
    const chartRefs = useRef({});
    const [personalityData, setPersonalityData] = useState([]);
    const [personalityJobTitles, setPersonalityJobTitles] = useState([]);
    const [allSubmittedAnswers, setAllSubmittedAnswers] = useState([]);
    const [filteredSubmittedAnswers, setFilteredSubmittedAnswers] = useState(null);
    const [currentJobId, setCurrentJobId] = useState(null);
    const [jobQuestions, setJobQuestions] = useState([]);
    const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);
    const [isLoadingPersonality, setIsLoadingPersonality] = useState(false);
    
    // Sub-modal state for non-primary answers
    const [standaloneModal, setStandaloneModal] = useState({
        show: false,
        jobId: null,
        openingPost: null,
        jobTitle: '',
        score: 0
    });

    useEffect(() => {
        return () => {
            Object.values(chartRefs.current).forEach(chart => chart?.destroy());
            chartRefs.current = {};
        };
    }, []);

    useEffect(() => {
        if (show && applicant?.id) {
            fetchPersonalityData(applicant.id);
        }
    }, [show, applicant]);
    
    useEffect(() => {
        Object.values(chartRefs.current).forEach(chart => chart?.destroy());
        chartRefs.current = {};

        if (Array.isArray(personalityData) && personalityData.length > 0) {
            personalityData.forEach((data, index) => {
                const chartId = `personalityChart-${applicant?.id}-${index}`;
                const chartElement = document.getElementById(chartId);
                if (chartElement && data?.job_id) {
                    const newChart = createDonutChart(data, chartId);
                    if (newChart) chartRefs.current[index] = newChart;
                }
            });
        }
    }, [personalityData, applicant?.id]);

    useEffect(() => {
        if (allSubmittedAnswers.length > 0 && currentJobId !== null) {
            const filtered = allSubmittedAnswers.find(item => item.job_id === currentJobId || item.posting_id === currentJobId);
            setFilteredSubmittedAnswers(filtered || null);
        } else {
            setFilteredSubmittedAnswers(null);
        }
    }, [allSubmittedAnswers, currentJobId]);

    const handleStatusChange = async (id, newStatus) => {
        try {
            await axios.put(`https://raasbackend.vetrinahealthcare.com/applicants/update_status/${id}/`, { 
                job_status: newStatus 
            });
            
            setSuccessMessage("Job status updated successfully");
            updateJobApplication(id, newStatus);
            
            setTimeout(() => setSuccessMessage(""), 5000);
        } catch (error) {
            console.error("Error updating job status:", error);
            alert("Failed to update job status. Please try again.");
        }
    };

    const fetchPersonalityData = async (applicantId) => {
        if (!applicantId) return;
        
        setIsLoadingPersonality(true);
        try {
            const personalityResponse = await axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`);
            
            const responseData = personalityResponse.data;
            let fetchedPersonalityData = [];
            
            if (responseData && responseData.results) {
                fetchedPersonalityData = responseData.results || [];
            } else if (Array.isArray(responseData)) {
                fetchedPersonalityData = responseData;
            }
            
            setPersonalityData(fetchedPersonalityData);

            if (fetchedPersonalityData.length > 0) {
                const jobTitles = fetchedPersonalityData.map(data => {
                    if (data.job_title) return data.job_title;
                    else if (data.opening_details?.jobtitle) return data.opening_details.jobtitle;
                    else return `Job ID: ${data.job_id || data.posting_id}`;
                });
                setPersonalityJobTitles(jobTitles);
            }
        } catch (error) {
            console.error("Error fetching personality data:", error);
        } finally {
            setIsLoadingPersonality(false);
        }
    };
    
    const handleViewAnswersAction = async (jobId, openingPost, jobTitle, score) => {
        // DETERMINISTIC LOGIC: primary job goes to bottom, others go to dialog
        const isPrimary = String(jobId) === String(primaryJobId);

        if (isPrimary) {
            // Scroll to bottom after state updates
            setCurrentJobId(jobId);
            handleShowSubmittedAnswers(applicant.id, jobId);
            
            setTimeout(() => {
                const bottomElement = document.getElementById('submitted-answers-section');
                if (bottomElement) {
                    bottomElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 500);
        } else {
            // Open standalone dialog
            setStandaloneModal({
                show: true,
                jobId,
                openingPost,
                jobTitle,
                score
            });
        }
    };

    const handleShowSubmittedAnswers = async (applicantId, jobId) => {
        setCurrentJobId(jobId);
        setIsLoadingAnswers(true);
        try {
            const answersResponse = await axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`);
            
            let answersData = [];
            const answersResponseData = answersResponse.data;
            
            if (answersResponseData && answersResponseData.results) {
                answersData = answersResponseData.results || [];
            } else if (Array.isArray(answersResponseData)) {
                answersData = answersResponseData;
            }
            
            setAllSubmittedAnswers(answersData);
            
            const currentAssessment = answersData.find(item => 
                item.job_id === jobId || item.posting_id === jobId
            );
            
            if (!currentAssessment) {
                setJobQuestions([]);
                setIsLoadingAnswers(false);
                return;
            }
            
            const openingPost = currentAssessment.opening_post;
            
            if (!openingPost) {
                setJobQuestions([]);
                setIsLoadingAnswers(false);
                return;
            }
            
            const questionResponse = await axios.get(
                `https://raasbackend.vetrinahealthcare.com/questions/${openingPost}/${jobId}/?applicant_id=${applicantId}`
            );
            
            const questionsData = questionResponse.data;
            if (questionsData && questionsData.questions) {
                setJobQuestions(questionsData.questions || []);
            } else if (Array.isArray(questionsData)) {
                setJobQuestions(questionsData);
            } else {
                setJobQuestions([]);
            }
            
        } catch (error) {
            console.error("Error fetching data:", error);
            setJobQuestions([]);
        } finally {
            setIsLoadingAnswers(false);
        }
    };
    
    const createDonutChart = (data, chartId) => {
        const ctx = document.getElementById(chartId)?.getContext("2d");
        if (!ctx || !data) return null;
        
        const correctCount = data.correct_count || 0;
        const wrongCount = data.wrong_count || 0;
        const total = correctCount + wrongCount;
        
        if (total === 0) return null;

        return new Chart(ctx, {
            type: 'doughnut',
            data: {
                labels: [`Correct (${correctCount})`, `Wrong (${wrongCount})`],
                datasets: [{
                    data: [correctCount, wrongCount],
                    backgroundColor: ['rgba(40, 167, 69, 0.8)', 'rgba(220, 53, 69, 0.8)'],
                    borderColor: ['rgba(40, 167, 69, 1)', 'rgba(220, 53, 69, 1)'],
                    borderWidth: 1
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '60%',
                plugins: { legend: { display: false } }
            }
        });
    };

    const renderAnswerSummary = (question, index) => {
        const userAnswer = question.user_selected_answer;
        
        if (!userAnswer) {
            return (
                <div className="text-center p-3">
                    <Badge bg="secondary">No answer data available</Badge>
                </div>
            );
        }

        const selectedIndex = userAnswer.selected_index;
        const isCorrect = userAnswer.is_correct === "correct";
        const correctIndex = question.correct_answer_index;
        const options = question.answers;

        return (
            <div className="p-2">
                <div className="mb-1">
                    <small className="text-muted fw-bold">Options:</small>
                </div>
                <div className="row g-2">
                    {options.map((option, idx) => {
                        const isSelected = idx === selectedIndex;
                        const isCorrectOpt = idx === correctIndex;
                        
                        let cardClass = "border bg-light";
                        let ringClass = "bg-light text-dark border";
                        
                        if (isSelected && isCorrectOpt) {
                            cardClass = "border-success bg-success bg-opacity-10";
                            ringClass = "bg-success text-white";
                        } else if (isSelected && !isCorrectOpt) {
                            cardClass = "border-danger bg-danger bg-opacity-10";
                            ringClass = "bg-danger text-white";
                        } else if (isCorrectOpt) {
                            cardClass = "border-success bg-success bg-opacity-5";
                            ringClass = "bg-success text-white";
                        }

                        return (
                            <div key={idx} className="col-12 col-md-6">
                                <div className={`d-flex align-items-center p-2 rounded h-100 ${cardClass}`}>
                                    <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm ${ringClass}`}
                                         style={{ width: '28px', height: '28px', fontSize: '13px', flexShrink: 0 }}>
                                        {String.fromCharCode(65 + idx)}
                                    </div>
                                    <div className="flex-grow-1 small">
                                        <span>{option}</span>
                                    </div>
                                    <div className="ms-2">
                                        {isSelected && isCorrectOpt && <i className="fas fa-check-circle text-success h5 mb-0"></i>}
                                        {isSelected && !isCorrectOpt && <i className="fas fa-times-circle text-danger h5 mb-0"></i>}
                                        {!isSelected && isCorrectOpt && <i className="fas fa-info-circle text-success h5 mb-0" title="Correct Answer"></i>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                <div className="mt-3 pt-2 border-top d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-4">
                        <div className="small">
                            <span className="text-muted me-2">Your selection:</span>
                            <Badge bg={isCorrect ? "success" : "danger"}>Option {String.fromCharCode(65 + selectedIndex)}</Badge>
                        </div>
                        {!isCorrect && (
                            <div className="small">
                                <span className="text-muted me-2">Correct answer:</span>
                                <Badge bg="success">Option {String.fromCharCode(65 + correctIndex)}</Badge>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <>
            <Modal show={show} onHide={onHide} dialogClassName="modal-xl" centered scrollable backdrop="static">
                <Modal.Header closeButton className="bg-primary text-white py-3 shadow-sm">
                    <Modal.Title className="d-flex align-items-center">
                        <div className="bg-white rounded-circle p-2 me-3 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px' }}>
                            <i className="fas fa-chart-line text-primary"></i>
                        </div>
                        <div>
                            <span className="h5 mb-0 d-block">{applicant?.first_name || 'Applicant'} Assessment Results</span>
                            <small className="opacity-75" style={{ fontSize: '0.8rem' }}>Profile Data & Historical Technical Assessments</small>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0 bg-light">
                    <div className="p-4">
                        {/* Job Applications Section */}
                        <div className="mb-5">
                            <div className="d-flex align-items-center mb-3">
                                <i className="fas fa-briefcase text-secondary me-2 h4 mb-0"></i>
                                <h5 className="mb-0 fw-bold">Active Job Applications</h5>
                            </div>
                            <Card className="shadow-sm border-0 overflow-hidden">
                                <Table hover responsive className="mb-0">
                                    <thead className="bg-dark text-white border-0">
                                        <tr>
                                            <th className="py-3 px-4 fw-medium border-0">Job Title</th>
                                            <th className="py-3 px-4 fw-medium border-0">Status Check</th>
                                            <th className="py-3 px-4 fw-medium border-0">Applied Date</th>
                                            <th className="py-3 px-4 fw-medium border-0 text-center">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white">
                                        {jobapplications.length > 0 ? (
                                            jobapplications.map(jobApp => {
                                                const statusOption = jobStatusOptions.find(opt => opt.value === jobApp.job_status);
                                                const assessment = personalityData.find(pd => pd.job_id === jobApp.posting_id || pd.posting_id === jobApp.posting_id);
                                                
                                                return (
                                                    <tr key={jobApp.id} className="align-middle border-bottom">
                                                        <td className="px-4 py-3 fw-bold text-dark">{jobApp.job_title || 'N/A'}</td>
                                                        <td className="px-4 py-3">
                                                            <Form.Select 
                                                                size="sm" 
                                                                className={`rounded-pill px-3 py-1 ${statusOption?.bgColor || ''}`}
                                                                value={jobApp.job_status} 
                                                                onChange={(e) => handleStatusChange(jobApp.id, e.target.value)}
                                                                style={{ 
                                                                    color: statusOption?.bgColor.includes('text-dark') ? '#000' : '#fff',
                                                                    minWidth: '160px',
                                                                    border: 'none',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                {jobStatusOptions.map(option => (
                                                                    <option key={option.value} value={option.value} style={{ background: '#fff', color: '#000' }}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </td>
                                                        <td className="px-4 py-3 text-muted">
                                                            <i className="far fa-calendar-alt me-2"></i>
                                                            {jobApp.applied_date ? new Date(jobApp.applied_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A' }
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            {assessment ? (
                                                                <Button 
                                                                    variant="outline-info" 
                                                                    size="sm" 
                                                                    className="rounded-pill px-3 shadow-none fw-bold"
                                                                    onClick={() => handleViewAnswersAction(assessment.job_id || assessment.posting_id, assessment.opening_post, jobApp.job_title, assessment.status)}
                                                                >
                                                                    <i className="fas fa-poll-h me-2"></i>View Assessment
                                                                </Button>
                                                            ) : (
                                                                <Badge bg="light" text="muted" className="border">Pending</Badge>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center p-5 text-muted">
                                                    <div className="mb-2"><i className="fas fa-folder-open h2 opacity-25"></i></div>
                                                    No job applications found for this profile.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </Card>
                        </div>

                        {/* Personality Assessments Grid */}
                        <div className="mb-5">
                            <div className="d-flex align-items-center mb-3">
                                <i className="fas fa-brain text-secondary me-2 h4 mb-0"></i>
                                <h5 className="mb-0 fw-bold">Performance Breakdown</h5>
                            </div>
                            
                            {isLoadingPersonality ? (
                                <div className="text-center p-5 bg-white rounded shadow-sm">
                                    <Spinner animation="grow" variant="primary" size="sm" />
                                    <Spinner animation="grow" variant="primary" size="sm" className="mx-2" />
                                    <Spinner animation="grow" variant="primary" size="sm" />
                                    <p className="mt-3 mb-0 text-muted">Analyzing assessment data...</p>
                                </div>
                            ) : personalityData.length > 0 ? (
                                <div className="row g-4">
                                    {personalityData.map((data, index) => {
                                        const jobId = data.job_id || data.posting_id;
                                        const jobTitle = personalityJobTitles[index] || `Assessment ${index + 1}`;
                                        const isPrimary = String(jobId) === String(primaryJobId);
                                        
                                        return data && jobId ? (
                                            <div key={`${jobId}-${index}`} className="col-12 col-md-6 col-lg-4">
                                                <Card className={`h-100 shadow-sm border-0 transition-hover ${isPrimary ? 'border-primary border-top border-4' : ''}`}>
                                                    <Card.Header className="bg-white border-0 pt-3 px-3 d-flex justify-content-between align-items-center">
                                                        <Badge bg={isPrimary ? "primary" : "secondary"} className="text-uppercase" style={{ fontSize: '0.65rem' }}>
                                                            {isPrimary ? 'Applied Job' : 'Historical'}
                                                        </Badge>
                                                        <small className="text-muted fw-bold">ID: #{jobId}</small>
                                                    </Card.Header>
                                                    <Card.Body className="p-3 text-center d-flex flex-column">
                                                        <div className="mb-3">
                                                            <Card.Title as="h6" className="text-truncate fw-bold mb-1" title={jobTitle}>
                                                                {jobTitle}
                                                            </Card.Title>
                                                            <div className="small text-muted mb-3">Technical Proficiency Assessment</div>
                                                        </div>
                                                        
                                                        <div className="flex-grow-1 position-relative mb-4" style={{ height: '140px' }}>
                                                            <canvas id={`personalityChart-${applicant?.id}-${index}`}></canvas>
                                                            <div className="position-absolute top-50 start-50 translate-middle text-center">
                                                                <h3 className="mb-0 fw-bold">{data.status ? `${data.status.toFixed(0)}%` : 'N/A'}</h3>
                                                                <small className="text-muted d-block" style={{ fontSize: '0.6rem' }}>OVERALL</small>
                                                            </div>
                                                        </div>

                                                        <div className="d-flex justify-content-around bg-light rounded p-2 mb-3">
                                                            <div className="text-center">
                                                                <div className="fw-bold text-success">{data.correct_count || 0}</div>
                                                                <div style={{ fontSize: '0.65rem' }} className="text-muted text-uppercase">Correct</div>
                                                            </div>
                                                            <div style={{ width: '1px' }} className="bg-secondary opacity-25"></div>
                                                            <div className="text-center">
                                                                <div className="fw-bold text-danger">{data.wrong_count || 0}</div>
                                                                <div style={{ fontSize: '0.65rem' }} className="text-muted text-uppercase">Wrong</div>
                                                            </div>
                                                        </div>

                                                        <Button 
                                                            variant={isPrimary ? "primary" : "outline-primary"} 
                                                            size="sm" 
                                                            className="w-100 rounded-pill fw-bold p-2"
                                                            onClick={() => handleViewAnswersAction(jobId, data.opening_post, jobTitle, data.status)}
                                                            disabled={isLoadingAnswers && currentJobId === jobId}
                                                        >
                                                            {isLoadingAnswers && currentJobId === jobId ? (
                                                                <Spinner size="sm" animation="border" />
                                                            ) : (
                                                                <><i className="fas fa-search-plus me-2"></i> {isPrimary ? 'Examine Detailed Answers' : 'View Historical Report'}</>
                                                            )}
                                                        </Button>
                                                    </Card.Body>
                                                </Card>
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                            ) : (
                                <div className="text-center p-5 bg-white rounded shadow-sm border border-dashed">
                                    <i className="fas fa-vial text-muted h1 opacity-25 mb-3"></i>
                                    <p className="text-muted fw-bold mb-0">No technical assessment data available for this candidate.</p>
                                </div>
                            )}
                        </div>

                        {/* Submitted Answers Section (Inline at bottom) */}
                        <div id="submitted-answers-section">
                            {currentJobId && (
                                <div className="mt-5 animate-slide-up">
                                    <div className="d-flex align-items-center mb-3">
                                        <i className="fas fa-tasks text-primary me-2 h4 mb-0"></i>
                                        <h5 className="mb-0 fw-bold">Primary Job Assessment: Detailed Breakdown</h5>
                                    </div>
                                    <Card className="shadow-lg border-0 overflow-hidden">
                                        <Card.Header className="bg-dark text-white border-0 py-3 shadow-sm">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center">
                                                    <i className="fas fa-clipboard-check me-3 h4 mb-0"></i>
                                                    <div>
                                                        <h5 className="mb-0 fw-bold">Question-wise Analysis</h5>
                                                        <small className="opacity-75">Review candidate choices and correct solutions</small>
                                                    </div>
                                                </div>
                                                <div className="text-end">
                                                    <Badge bg="primary" className="p-2 fs-6 rounded-pill border">
                                                        Accuracy: {filteredSubmittedAnswers?.status || 0}%
                                                    </Badge>
                                                </div>
                                            </div>
                                        </Card.Header>
                                        <Card.Body className="p-4 bg-white">
                                            {isLoadingAnswers ? (
                                                <div className="text-center p-5">
                                                    <Spinner animation="border" variant="primary" />
                                                    <div className="mt-3 text-muted">Retrieving assessment repository...</div>
                                                </div>
                                            ) : jobQuestions.length > 0 ? (
                                                <div className="row g-4">
                                                    {jobQuestions.map((question, index) => {
                                                        const userAnswer = question.user_selected_answer;
                                                        const isCorrect = userAnswer?.is_correct === "correct";
                                                        
                                                        return (
                                                            <div key={question.id || index} className="col-12">
                                                                <div className={`rounded-4 overflow-hidden border p-0 ${isCorrect ? 'border-success' : 'border-danger'}`} style={{ borderLeftWidth: '8px !important' }}>
                                                                    <div className={`p-2 px-3 fw-bold small text-white d-flex justify-content-between ${isCorrect ? 'bg-success' : 'bg-danger'}`}>
                                                                        <span>QUESTION {index + 1}</span>
                                                                        <span>{isCorrect ? 'CORRECT' : 'WRONG'}</span>
                                                                    </div>
                                                                    <div className="p-4">
                                                                        <div className="d-flex align-items-start gap-2 mb-4">
                                                                            <h6 className="fw-bold text-dark mb-0 lh-base text-start" style={{ fontSize: '1.05rem' }}>
                                                                                {question.question_text}
                                                                            </h6>
                                                                        </div>
                                                                        {renderAnswerSummary(question, index)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="text-center p-5 border rounded bg-light border-dashed">
                                                    <i className="fas fa-ghost text-muted h2 opacity-25 mb-3 d-block"></i>
                                                    No specific question data available for this assessment ID.
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </div>
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-light shadow-inner py-3">
                    {/* <Button variant="secondary" onClick={onHide} className="rounded-pill px-4 fw-bold">
                        <i className="fas fa-times me-2"></i> Close Report
                    </Button> */}
                </Modal.Footer>
            </Modal>

            {/* STANDALONE DIALOG FOR HISTORICAL/NON-PRIMARY ANSWERS */}
            <StandaloneAnswersModal
                show={standaloneModal.show}
                onHide={() => setStandaloneModal(prev => ({ ...prev, show: false }))}
                applicant={applicant}
                jobId={standaloneModal.jobId}
                openingPost={standaloneModal.openingPost}
                jobTitle={standaloneModal.jobTitle}
                score={standaloneModal.score}
            />
        </>
    );
};




// --- MAIN INTERVIEWER DASHBOARD COMPONENT ---
function InterviewerDashboard() {
    const [interviewSchedule, setInterviewSchedule] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [searchQuery, setSearchQuery] = useState("");
    const [loadingTable, setLoadingTable] = useState(true);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [loadingView, setLoadingView] = useState({});
    const [loadingResume, setLoadingResume] = useState({});
    const [selectedApplicantForResults, setSelectedApplicantForResults] = useState(null);
    const [jobApplicationsForModal, setJobApplicationsForModal] = useState([]);
    const [primaryJobIdForModal, setPrimaryJobIdForModal] = useState(null);
    const [loadingResults, setLoadingResults] = useState({});
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => { 
        fetchInterviewSchedule(); 
    }, []);

    const fetchInterviewSchedule = async () => {
        const storedEmail = localStorage.getItem("email");
        if (!storedEmail) {
            setLoadingTable(false);
            return;
        }
        setLoadingTable(true);
        try {
            // First fetch user profile to get the canonical email
            const profileRes = await axios.post("https://raasbackend.vetrinahealthcare.com/UserProfile/", { email: storedEmail });
            const emailFromProfile = profileRes.data.user_data?.email;

            if (!emailFromProfile) {
                console.error("Email not found in UserProfile response");
                setLoadingTable(false);
                return;
            }

            // Now fetch interviews specifically for this email
            const response = await axios.get(`https://raasbackend.vetrinahealthcare.com/interviews/${emailFromProfile}/`);
            
            const fetchedData = Array.isArray(response.data) ? response.data : 
                               (response.data.interviews || []); // Handle different response formats

            const formattedData = fetchedData.map(interview => ({
                ...interview,
                date: formatDate(interview.date),
                time: formatTime(interview.time)
            }));
            
            setInterviewSchedule(formattedData);
        } catch (error) {
            console.error("Error fetching schedule:", error);
        } finally {
            setLoadingTable(false);
        }
    };

    const handleViewResultsClick = async (interview) => {
        const email = interview.candidate_email;
        if (!email) return;
        
        setLoadingResults(prev => ({ ...prev, [email]: true }));
        try {
            const applicantRes = await axios.post("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { email });
            
            if (!applicantRes.data || !applicantRes.data.id) {
                alert("Applicant data not found");
                return;
            }
            
            setSelectedApplicantForResults(applicantRes.data);
            setPrimaryJobIdForModal(interview.posting_id); // This is our 'specific job' anchor
            
            // Fetch job applications for this applicant
            const jobsRes = await axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantRes.data.id}/`);
            setJobApplicationsForModal(jobsRes.data || []);
            
            // Show the results modal
            setShowResultsModal(true);
        } catch (error) {
            console.error("Error fetching data for results modal:", error);
            alert("Failed to load applicant results. Please try again.");
        } finally {
            setLoadingResults(prev => ({ ...prev, [email]: false }));
        }
    };
    
    const updateJobApplicationStatusInList = (jobAppId, newStatus) => {
        setJobApplicationsForModal(prev => 
            prev.map(app => app.id === jobAppId ? { ...app, job_status: newStatus } : app)
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-GB');
        } catch (e) {
            return dateString;
        }
    };
    
    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        try {
            const [hours, minutes] = timeString.split(':');
            const hour = parseInt(hours, 10);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const hour12 = hour % 12 || 12;
            return `${hour12}:${minutes} ${ampm}`;
        } catch (e) {
            return timeString;
        }
    };

    const handleViewClick = async (email) => {
        setLoadingView(prev => ({ ...prev, [email]: true }));
        try {
            const response = await axios.post("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { email });
            
            if (response.data) {
                setSelectedApplicant(response.data);
                setShowProfileModal(true);
            } else {
                alert("No applicant data found");
            }
        } catch (error) {
            console.error("Error fetching applicant data:", error);
            alert("Failed to load applicant details. Please try again.");
        } finally {
            setLoadingView(prev => ({ ...prev, [email]: false }));
        }
    };

    const handleViewResume = async (email) => {
        setLoadingResume(prev => ({ ...prev, [email]: true }));
        try {
            const response = await axios.post(
                "https://raasbackend.vetrinahealthcare.com/view_resume/", 
                { email },
                { responseType: 'blob' }
            );
            
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error fetching resume:", error);
            alert("Failed to load resume. Please try again.");
        } finally {
            setLoadingResume(prev => ({ ...prev, [email]: false }));
        }
    };

    const filteredSchedule = interviewSchedule.filter(i =>
        Object.values(i).some(val =>
            String(val).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const totalItems = filteredSchedule.length;
    const totalPages = Math.ceil(totalItems / rowsPerPage);
    const safeCurrentPage = Math.min(currentPage, totalPages || 1);
    const startIndex = (safeCurrentPage - 1) * rowsPerPage;
    const currentItems = filteredSchedule.slice(startIndex, startIndex + rowsPerPage);
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const getMeetingBadge = (interview) => {
        if (interview.meetLink) {
            return (
                <a
                    href={interview.meetLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        display: 'inline-block',
                        padding: '4px 14px',
                        borderRadius: '20px',
                        backgroundColor: '#0d6efd',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap'
                    }}
                >
                    Join Meeting
                </a>
            );
        }
        const mode = (interview.mode || 'Offline').toLowerCase();
        const bgColor = mode === 'online' ? '#6c757d' : '#6c757d';
        return (
            <span style={{
                display: 'inline-block',
                padding: '4px 14px',
                borderRadius: '20px',
                backgroundColor: bgColor,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.78rem',
                whiteSpace: 'nowrap'
            }}>
                {interview.mode || 'Offline'}
            </span>
        );
    };

    return (
        <>
            <DashNavbar />
            <div className="wrapper">
                <Sidebar />
                <div className="col-lg-10 col-md-8 col-sm-12 main dash4">
                    <div style={{ padding: '24px 28px' }}>

                        {successMessage && (
                            <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
                                {successMessage}
                            </Alert>
                        )}

                        {/* Welcome Banner */}
                        <div style={{
                            background: '#fff',
                            borderRadius: '12px',
                            padding: '28px 32px',
                            marginBottom: '24px',
                            boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                            textAlign: 'left',
                            borderLeft: '5px solid #f97316'
                        }}>
                            <h2 style={{ fontWeight: 700, fontSize: '1.8rem', marginBottom: '8px', color: '#1a1a2e' }}>
                                Welcome to your Dashboard
                            </h2>
                            <p style={{ color: '#555', fontSize: '0.97rem', margin: 0 }}>
                                Below is your interview schedule. You currently have{' '}
                                <strong>{totalItems}</strong> candidate(s) to review.
                            </p>
                        </div>

                        {/* Table Card */}
                        <div style={{
                            background: '#fff',
                            borderRadius: '12px',
                            padding: '24px 28px',
                            boxShadow: '0 1px 6px rgba(0,0,0,0.07)'
                        }}>

                            {/* Header row: title + search */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '18px',
                                flexWrap: 'wrap',
                                gap: '12px'
                            }}>
                                <h4 style={{ fontWeight: 700, color: '#0d6efd', margin: 0 }}>
                                    Interview Schedule
                                </h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '280px' }}>
                                    <span style={{ color: '#aaa', fontSize: '1.1rem' }}>&#128269;</span>
                                    <input
                                        type="search"
                                        placeholder="Search by name, email, job title..."
                                        value={searchQuery}
                                        onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                        style={{
                                            border: '1px solid #dee2e6',
                                            borderRadius: '8px',
                                            padding: '7px 14px',
                                            fontSize: '0.88rem',
                                            outline: 'none',
                                            width: '100%',
                                            color: '#444',
                                            background: '#fff'
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Table */}
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                            {['SR.NO','CANDIDATE','DATE','TIME','APPLIED JOB','OPPORTUNITY TYPE','MEETING','ACTIONS'].map(h => (
                                                <th key={h} style={{
                                                    padding: '10px 12px',
                                                    textAlign: 'center',
                                                    fontWeight: 700,
                                                    color: '#333',
                                                    fontSize: '0.78rem',
                                                    letterSpacing: '0.03em',
                                                    background: '#fff',
                                                    borderBottom: '2px solid #e9ecef',
                                                    whiteSpace: 'normal',
                                                    lineHeight: '1.2',
                                                    verticalAlign: 'middle'
                                                }}>
                                                    {h.split(' ').map((word, i) => (
                                                        <div key={i}>{word}</div>
                                                    ))}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loadingTable ? (
                                            <tr>
                                                <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                                                    <Spinner animation="border" variant="primary" />
                                                    <p style={{ marginTop: '8px', color: '#888' }}>Loading schedule...</p>
                                                </td>
                                            </tr>
                                        ) : currentItems.length > 0 ? (
                                            currentItems.map((interview, index) => {
                                                const rowNum = startIndex + index + 1;
                                                const isEven = index % 2 === 1;
                                                return (
                                                    <tr key={interview.id || index} style={{ background: isEven ? '#f9fafb' : '#fff', borderBottom: '1px solid #f0f0f0' }}>
                                                        <td style={{ padding: '11px 12px', textAlign: 'center', color: '#444' }}>{rowNum}</td>
                                                        <td style={{ padding: '11px 12px', textAlign: 'center', fontWeight: 600, color: '#222', maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                            title={interview.candidate}>
                                                            {interview.candidate && interview.candidate.length > 15
                                                                ? interview.candidate.substring(0, 15) + ' ...'
                                                                : interview.candidate}
                                                        </td>
                                                        <td style={{ padding: '11px 12px', textAlign: 'center', color: '#444' }}>{interview.date}</td>
                                                        <td style={{ padding: '11px 12px', textAlign: 'center', color: '#444', fontWeight: 600 }}>{interview.time}</td>
                                                        <td style={{ padding: '11px 12px', textAlign: 'center', color: '#444' }}>{interview.job_title || 'N/A'}</td>
                                                        <td style={{ padding: '11px 12px', textAlign: 'center', color: '#444' }}>{formatOpportunityType(interview.opening_post)}</td>
                                                        <td style={{ padding: '11px 12px', textAlign: 'center' }}>
                                                            {getMeetingBadge(interview)}
                                                        </td>
                                                        <td style={{ padding: '11px 12px', textAlign: 'center' }}>
                                                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                                                <button
                                                                    onClick={() => handleViewClick(interview.candidate_email)}
                                                                    disabled={loadingView[interview.candidate_email]}
                                                                    style={{
                                                                        padding: '4px 11px',
                                                                        fontSize: '0.78rem',
                                                                        border: '1px solid #ced4da',
                                                                        borderRadius: '6px',
                                                                        background: '#fff',
                                                                        color: '#444',
                                                                        cursor: 'pointer',
                                                                        fontWeight: 500,
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    {loadingView[interview.candidate_email]
                                                                        ? <Spinner size="sm" animation="border" />
                                                                        : 'View Profile'}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleViewResultsClick(interview)}
                                                                    disabled={loadingResults[interview.candidate_email]}
                                                                    style={{
                                                                        padding: '4px 11px',
                                                                        fontSize: '0.78rem',
                                                                        border: 'none',
                                                                        borderRadius: '6px',
                                                                        background: '#0dcaf0',
                                                                        color: '#fff',
                                                                        cursor: 'pointer',
                                                                        fontWeight: 600,
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    {loadingResults[interview.candidate_email]
                                                                        ? <Spinner size="sm" animation="border" />
                                                                        : 'Results'}
                                                                </button>
                                                                <button
                                                                    onClick={() => handleViewResume(interview.candidate_email)}
                                                                    disabled={loadingResume[interview.candidate_email]}
                                                                    style={{
                                                                        padding: '4px 11px',
                                                                        fontSize: '0.78rem',
                                                                        border: '1px solid #ced4da',
                                                                        borderRadius: '6px',
                                                                        background: '#fff',
                                                                        color: '#444',
                                                                        cursor: 'pointer',
                                                                        fontWeight: 500,
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    {loadingResume[interview.candidate_email]
                                                                        ? <Spinner size="sm" animation="border" />
                                                                        : 'Resume'}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                                                    No interviews scheduled.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Bar */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginTop: '18px',
                                flexWrap: 'wrap',
                                gap: '10px',
                                fontSize: '0.85rem',
                                color: '#555'
                            }}>
                                {/* Showing info */}
                                <div>
                                    Showing{' '}
                                    <strong>{totalItems === 0 ? 0 : startIndex + 1}</strong>{' '}to{' '}
                                    <strong>{endIndex}</strong>{' '}of{' '}
                                    <strong>{totalItems}</strong>{' '}results
                                </div>

                                {/* Rows per page + page nav */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>Rows per page:</span>
                                        <select
                                            value={rowsPerPage}
                                            onChange={handleRowsPerPageChange}
                                            style={{
                                                border: '1px solid #dee2e6',
                                                borderRadius: '6px',
                                                padding: '3px 8px',
                                                fontSize: '0.85rem',
                                                color: '#444',
                                                background: '#fff'
                                            }}
                                        >
                                            {[5, 10, 15, 25, 50].map(n => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>Page <strong>{safeCurrentPage}</strong> of <strong>{totalPages || 1}</strong></span>
                                        <button
                                            onClick={() => setCurrentPage(1)}
                                            disabled={safeCurrentPage === 1}
                                            style={navBtnStyle(safeCurrentPage === 1)}
                                            title="First page"
                                        >&laquo;</button>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                                            disabled={safeCurrentPage === 1}
                                            style={navBtnStyle(safeCurrentPage === 1)}
                                            title="Previous page"
                                        >&#8249;</button>
                                        <button
                                            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                                            disabled={safeCurrentPage === totalPages || totalPages === 0}
                                            style={navBtnStyle(safeCurrentPage === totalPages || totalPages === 0)}
                                            title="Next page"
                                        >&#8250;</button>
                                        <button
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={safeCurrentPage === totalPages || totalPages === 0}
                                            style={navBtnStyle(safeCurrentPage === totalPages || totalPages === 0)}
                                            title="Last page"
                                        >&raquo;</button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* Profile Details Modal */}
            <ApplicantDetailsModal
                applicant={selectedApplicant || {}}
                show={showProfileModal}
                onHide={() => setShowProfileModal(false)}
            />

            {/* Results Modal */}
            {showResultsModal && (
                <ApplicantModal
                    show={showResultsModal}
                    onHide={() => setShowResultsModal(false)}
                    applicant={selectedApplicantForResults}
                    jobapplications={jobApplicationsForModal}
                    setSuccessMessage={setSuccessMessage}
                    updateJobApplication={updateJobApplicationStatusInList}
                    primaryJobId={primaryJobIdForModal}
                />
            )}
        </>
    );
}

const navBtnStyle = (disabled) => ({
    padding: '4px 10px',
    fontSize: '0.88rem',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    background: disabled ? '#f8f9fa' : '#fff',
    color: disabled ? '#aaa' : '#444',
    cursor: disabled ? 'not-allowed' : 'pointer',
    lineHeight: 1.4
});

export default InterviewerDashboard;

// import React, { useState, useEffect, useRef, useMemo } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import ApplicantDetailsModal from "../ProfileForm/ApplicantDetailsModal";
// import "../css/dash.css";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../css/PDFViewer.css";
// import { Link } from "react-router-dom";
// import { Pagination, Spinner, Button, Modal, Card, Table, Form, Alert, InputGroup } from "react-bootstrap";
// import axios from 'axios';
// import Chart from "chart.js/auto";

// // --- GLOBAL OPTIONS FOR MODAL ---
// // We define this here so the ApplicantModal below can access it.
// const jobStatusOptions = [
//     { value: "applied", label: "Applied", bgColor: "bg-info" },
//     { value: "under_review", label: "Under Review", bgColor: "bg-secondary" },
//     { value: "interview_scheduled", label: "Interview Scheduled", bgColor: "bg-warning text-dark" },
//     { value: "selected", label: "Selected", bgColor: "bg-success" },
//     { value: "rejected", label: "Rejected", bgColor: "bg-danger" },
// ];


// // --- APPLICANT MODAL COMPONENT (DEFINED IN-FILE) ---
// // By defining this component here, we don't need to create a separate file for it.
// const ApplicantModal = ({ show, onHide, applicant, jobapplications, setSuccessMessage, updateJobApplication }) => {
//     const chartRefs = useRef({});
//     const [personalityData, setPersonalityData] = useState([]);
//     const [personalityJobTitles, setPersonalityJobTitles] = useState([]);
//     const [allSubmittedAnswers, setAllSubmittedAnswers] = useState(null);
//     const [filteredSubmittedAnswers, setFilteredSubmittedAnswers] = useState(null);
//     const [currentJobId, setCurrentJobId] = useState(null);
//     const [jobQuestions, setJobQuestions] = useState(null);
//     const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);

//     useEffect(() => {
//         // Cleanup when modal closes or applicant changes
//         return () => {
//             Object.values(chartRefs.current).forEach(chart => chart?.destroy());
//             chartRefs.current = {};
//         };
//     }, [applicant]);

//     useEffect(() => {
//         if (show && applicant?.id) {
//             fetchPersonalityData(applicant.id);
//         }
//     }, [show, applicant]);
    
//     useEffect(() => {
//         // Redraw charts when data is available
//         Object.values(chartRefs.current).forEach(chart => chart?.destroy());
//         chartRefs.current = {};

//         if (Array.isArray(personalityData)) {
//             personalityData.forEach((data, index) => {
//                 const chartId = `personalityChart-${applicant?.id}-${index}`;
//                 const chartElement = document.getElementById(chartId);
//                 if (chartElement && data?.job_id) {
//                     const newChart = createDonutChart(data, chartId);
//                     if (newChart) chartRefs.current[index] = newChart;
//                 }
//             });
//         }
//     }, [personalityData, applicant?.id]);

//     useEffect(() => {
//         if (allSubmittedAnswers && currentJobId !== null) {
//             const filtered = allSubmittedAnswers.find(item => item.job_id === currentJobId);
//             setFilteredSubmittedAnswers(filtered || null);
//         } else {
//             setFilteredSubmittedAnswers(null);
//         }
//     }, [allSubmittedAnswers, currentJobId]);
    
//     const handleStatusChange = (id, newStatus) => {
//         axios.put(`https://raasbackend.vetrinahealthcare.com/applicants/update_status/${id}/`, { job_status: newStatus })
//             .then(() => {
//                 setSuccessMessage("Job status updated successfully");
//                 updateJobApplication(id, newStatus);
//                 setTimeout(() => setSuccessMessage(""), 5000);
//             }).catch(error => console.error("Error updating job status:", error));
//     };

//     const fetchPersonalityData = async (applicantId) => {
//         try {
//             const [personalityResponse, jobTitleResponse] = await Promise.all([
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`),
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantId}/`)
//             ]);
            
//             const fetchedPersonalityData = personalityResponse.data || [];
//             setPersonalityData(fetchedPersonalityData);

//             if (fetchedPersonalityData.length > 0) {
//                 const jobTitlesMap = (jobTitleResponse.data || []).reduce((acc, job) => {
//                     if (job.job_id) acc[job.job_id] = job.job_title;
//                     return acc;
//                 }, {});
//                 const jobTitles = fetchedPersonalityData.map(data => jobTitlesMap[data.job_id] || `Job ID: ${data.job_id}`);
//                 setPersonalityJobTitles(jobTitles);
//             }
//         } catch (error) {
//             console.error("Error fetching personality data:", error);
//         }
//     };
    
//     const handleShowSubmittedAnswers = async (applicantId, jobId) => {
//         setCurrentJobId(jobId);
//         setIsLoadingAnswers(true);
//         try {
//             const [answersResponse, questionResponse] = await Promise.all([
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`),
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/questions/${jobId}/`)
//             ]);
//             setAllSubmittedAnswers(answersResponse.data);
//             setJobQuestions(questionResponse.data || []);
//         } catch (error) {
//             console.error("Error fetching answers:", error);
//         } finally {
//             setIsLoadingAnswers(false);
//         }
//     };
    
//     const createDonutChart = (data, chartId) => {
//         const ctx = document.getElementById(chartId)?.getContext("2d");
//         if (!ctx || !data || typeof data.correct_count !== 'number' || typeof data.wrong_count !== 'number') return null;
        
//         return new Chart(ctx, {
//             type: 'doughnut',
//             data: {
//                 labels: [`Correct (${data.correct_count})`, `Wrong (${data.wrong_count})`],
//                 datasets: [{
//                     data: [data.correct_count, data.wrong_count],
//                     backgroundColor: ['rgba(40, 167, 69, 0.7)', 'rgba(220, 53, 69, 0.7)'],
//                 }]
//             },
//             options: { responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { display: false } } }
//         });
//     };

//     return (
//         <Modal show={show} onHide={onHide} dialogClassName="modal-xl" centered scrollable>
//             <Modal.Header closeButton><Modal.Title className="gradient-text">{applicant?.first_name || 'Applicant'} Details</Modal.Title></Modal.Header>
//             <Modal.Body className="p-4">
//                 <Card className="mb-4 shadow-sm"><Card.Header><h5 className="mb-0">Job Applications</h5></Card.Header>
//                     <Card.Body className="p-0">
//                         <Table striped bordered hover responsive size="sm" className="mb-0">
//                             <thead><tr><th>Job Title</th><th>Status</th><th>Applied On</th></tr></thead>
//                             <tbody>
//                                 {jobapplications.length > 0 ? jobapplications.map(jobApp => {
//                                     const statusOption = jobStatusOptions.find(opt => opt.value === jobApp.job_status);
//                                     return (
//                                         <tr key={jobApp.id}>
//                                             <td>{jobApp.job_title || 'N/A'}</td>
//                                             <td>
//                                                 <Form.Select size="sm" className={`${statusOption?.bgColor || ''}`} value={jobApp.job_status} onChange={(e) => handleStatusChange(jobApp.id, e.target.value)} style={{ color: statusOption?.bgColor.includes('text-dark') ? '#000' : '#fff' }}>
//                                                     {jobStatusOptions.map(option => <option key={option.value} value={option.value} style={{ backgroundColor: '#fff', color: '#000' }}>{option.label}</option>)}
//                                                 </Form.Select>
//                                             </td>
//                                             <td>{jobApp.applied_date ? new Date(jobApp.applied_date).toLocaleDateString() : 'N/A'}</td>
//                                         </tr>
//                                     );
//                                 }) : <tr><td colSpan="3" className="text-center p-3">No job applications found.</td></tr>}
//                             </tbody>
//                         </Table>
//                     </Card.Body>
//                 </Card>
//                 <h4 className="gradient-text text-center mt-4 mb-3">Personality Assessments</h4>
//                 <div className="d-flex flex-wrap justify-content-center gap-3">
//                     {personalityData.length > 0 ? personalityData.map((data, index) => (
//                         data && data.job_id ? (
//                             <Card key={`${data.job_id}-${index}`} className="text-center shadow-sm" style={{ minWidth: '260px', flex: '1 1 260px' }}>
//                                 <Card.Header><Card.Title as="h6" className="mb-0 text-truncate">{personalityJobTitles[index] || `Assessment ID: ${data.job_id}`}</Card.Title></Card.Header>
//                                 <Card.Body className="p-2">
//                                     <div style={{ height: '150px', width: '100%' }}><canvas id={`personalityChart-${applicant?.id}-${index}`}></canvas></div>
//                                     <p className="mb-1"><strong>Score:</strong> <b className="gradient-text">{typeof data.status === 'number' ? `${data.status.toFixed(1)}%` : 'N/A'}</b></p>
//                                     <Button variant="outline-info" size="sm" onClick={() => handleShowSubmittedAnswers(applicant.id, data.job_id)} disabled={isLoadingAnswers && currentJobId === data.job_id}>
//                                         {isLoadingAnswers && currentJobId === data.job_id ? <Spinner as="span" size="sm" /> : "View Answers"}
//                                     </Button>
//                                 </Card.Body>
//                             </Card>
//                         ) : null
//                     )) : <p className="text-center text-muted">No assessment data available.</p>}
//                 </div>
//                 {currentJobId && (
//                     <div className="mt-4"><Card className="shadow-sm"><Card.Header>
//                         <h5 className="mb-0">Submitted Answers for Job: {personalityJobTitles[personalityData.findIndex(p => p.job_id === currentJobId)] || `ID ${currentJobId}`}</h5>
//                     </Card.Header><Card.Body>
//                         {isLoadingAnswers ? <div className="text-center p-4"><Spinner /></div> : 
//                         filteredSubmittedAnswers && jobQuestions ? (
//                             <Table striped bordered responsive size="sm">
//                                 <thead><tr><th>#</th><th>Question</th><th>Your Answer</th><th>Result</th></tr></thead>
//                                 <tbody>
//                                     {jobQuestions.map((q, i) => {
//                                         const subAns = filteredSubmittedAnswers.selected_answers?.find(a => a.question_id === q.id);
//                                         const isCorrect = subAns && subAns.selected_answer_index === q.correct_answer_index;
//                                         return (
//                                             <tr key={q.id}>
//                                                 <td>{i + 1}</td><td>{q.question_text}</td>
//                                                 <td>{subAns ? (q.answers[subAns.selected_answer_index] || subAns.selected_answer_text) : <i>Not Answered</i>}</td>
//                                                 <td className="text-center">{subAns ? <span className={`badge ${isCorrect ? 'bg-success' : 'bg-danger'}`}>{isCorrect ? 'Correct' : 'Wrong'}</span> : <span className="badge bg-secondary">N/A</span>}</td>
//                                             </tr>
//                                         );
//                                     })}
//                                 </tbody>
//                             </Table>
//                         ) : <p className="text-center p-3">Could not load answers.</p>}
//                     </Card.Body></Card></div>
//                 )}
//             </Modal.Body>
//             <Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer>
//         </Modal>
//     );
// };


// // --- YOUR MAIN COMPONENT ---
// function InterviewerDashboard() {
//     const [interviewSchedule, setInterviewSchedule] = useState([]);
//     const [appliedJobs, setAppliedJobs] = useState({});
//     const [currentPage, setCurrentPage] = useState(1);
//     const itemsPerPage = 5;
//     const [searchQuery, setSearchQuery] = useState("");
//     const [loadingTable, setLoadingTable] = useState(true);
//     const [showModal, setShowModal] = useState(false);
//     const [selectedApplicant, setSelectedApplicant] = useState(null);
//     const [loadingView, setLoadingView] = useState({});
//     const [loadingResume, setLoadingResume] = useState({});

//     // State for the "Results" modal
//     const [showResultsModal, setShowResultsModal] = useState(false);
//     const [selectedApplicantForResults, setSelectedApplicantForResults] = useState(null);
//     const [jobApplicationsForModal, setJobApplicationsForModal] = useState([]);
//     const [loadingResults, setLoadingResults] = useState({});
//     const [successMessage, setSuccessMessage] = useState("");

//     useEffect(() => { fetchInterviewSchedule(); }, []);

//     const fetchInterviewSchedule = () => {
//         const userEmail = localStorage.getItem("email");
//         if (!userEmail) {
//             setLoadingTable(false);
//             return;
//         }
//         setLoadingTable(true);
//         // UPDATED: Fetch all interviews from the new GET endpoint
//         axios.get("https://raasbackend.vetrinahealthcare.com/interviews/")
//             .then(response => {
//                 const allInterviews = response.data;
//                 // Filter the interviews on the client-side for the logged-in interviewer
//                 const userInterviews = allInterviews.filter(interview => interview.interviewer_email === userEmail);
                
//                 const formattedData = userInterviews.map(interview => ({
//                     ...interview,
//                     date: formatDate(interview.date),
//                     time: formatTime(interview.time)
//                 }));
                
//                 setInterviewSchedule(formattedData);
//                 fetchAppliedJobsForSchedule(formattedData);
//             })
//             .catch(error => console.error("Error fetching schedule:", error))
//             .finally(() => setLoadingTable(false));
//     };

//     const fetchAppliedJobsForSchedule = (schedule) => {
//         schedule.forEach(interview => {
//             fetch(`https://raasbackend.vetrinahealthcare.com/get_applied_job/${interview.candidate_email}/`)
//                 .then(res => res.json())
//                 .then(data => setAppliedJobs(prev => ({ ...prev, [interview.candidate_email]: data.job_titles || ["N/A"] })))
//                 .catch(error => console.error("Error fetching applied jobs:", error));
//         });
//     };

//     const handleViewResultsClick = async (interview) => {
//         const email = interview.candidate_email;
//         if (!email) return;
//         setLoadingResults(prev => ({ ...prev, [email]: true }));
//         try {
//             const applicantRes = await axios.post("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { email });
//             if (!applicantRes.data || !applicantRes.data.id) throw new Error("Applicant ID not found.");
//             setSelectedApplicantForResults(applicantRes.data);
//             const jobsRes = await axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantRes.data.id}/`);
//             setJobApplicationsForModal(jobsRes.data || []);
//             setShowResultsModal(true);
//         } catch (error) {
//             console.error("Error fetching data for results modal:", error);
//         } finally {
//             setLoadingResults(prev => ({ ...prev, [email]: false }));
//         }
//     };
    
//     const updateJobApplicationStatusInList = (jobAppId, newStatus) => {
//         setJobApplicationsForModal(prev => prev.map(app => app.id === jobAppId ? { ...app, job_status: newStatus } : app));
//     };

//     const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-GB');
//     const formatTime = (timeString) => new Date(`01/01/2022 ${timeString}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

//     const handleViewClick = (email) => {
//         setLoadingView({ ...loadingView, [email]: true });
//         fetch("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) })
//             .then(res => res.json()).then(data => { setSelectedApplicant(data); setShowModal(true); })
//             .catch(err => console.error("Error fetching applicant data:", err))
//             .finally(() => setLoadingView({ ...loadingView, [email]: false }));
//     };

//     const handleViewResume = (email) => {
//         setLoadingResume({ ...loadingResume, [email]: true });
//         fetch("https://raasbackend.vetrinahealthcare.com/view_resume/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) })
//             .then(res => res.blob()).then(blob => window.open(URL.createObjectURL(blob), "_blank"))
//             .catch(err => console.error("Error fetching resume:", err))
//             .finally(() => setLoadingResume({ ...loadingResume, [email]: false }));
//     };

//     const filteredSchedule = interviewSchedule.filter(i => Object.values(i).some(val => String(val).toLowerCase().includes(searchQuery.toLowerCase())) || appliedJobs[i.candidate_email]?.some(j => j.toLowerCase().includes(searchQuery.toLowerCase())));
//     const currentItems = filteredSchedule.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
//     const totalPages = Math.ceil(filteredSchedule.length / itemsPerPage);

//     return (
//         <>
//             <DashNavbar />
//             <div className="wrapper">
//                 <Sidebar />
//                 <div className="col-lg-10 col-md-8 col-sm-12 main dash4">
//                     <div className="container mt-4">
//                         <div className="text-start"><h3>Welcome to your Dashboard</h3><p>Below is your interview schedule:</p></div>
//                         <div className="auth-inner mt-5 w-100" style={{ padding: "10px" }}>
//                             <h2 className="mb-4">Interview Schedule</h2>
//                             <div className="row mb-3"><div className="col-12 col-md-6 mx-auto"><InputGroup><Form.Control type="search" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></InputGroup></div></div>
//                             <div className="table-responsive">
//                                 <Table striped bordered hover size="sm">
//                                     <thead className="thead-dark"><tr><th>Sr.No</th><th>Candidate</th><th>Date</th><th>Time</th><th>Applied Job</th><th>Meeting</th><th>Actions</th></tr></thead>
//                                     <tbody>
//                                         {loadingTable ? (<tr><td colSpan="7" className="text-center p-4"><Spinner /></td></tr>)
//                                         : currentItems.length > 0 ? (currentItems.map((interview, index) => (
//                                             <tr key={interview.id || index}>
//                                                 <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                                                 <td>{interview.candidate}</td>
//                                                 <td>{interview.date}</td>
//                                                 <td>{interview.time}</td>
//                                                 <td>{interview.job_title || 'N/A'}</td> {/* Using job_title from interview data */}
//                                                 <td>
//                                                   {interview.meetLink ? (
//                                                     <a href={interview.meetLink} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">Join</a>
//                                                   ) : (
//                                                     <span>{interview.location || 'N/A'}</span> // Display location for offline interviews
//                                                   )}
//                                                 </td>
//                                                 <td>
//                                                     <div className="d-flex flex-sm-row flex-column">
//                                                         <Button variant="secondary" size="sm" className="me-sm-2 mb-2 mb-sm-0" onClick={() => handleViewClick(interview.candidate_email)} disabled={loadingView[interview.candidate_email]}>{loadingView[interview.candidate_email] ? <Spinner size="sm"/> : "View"}</Button>
//                                                         <Button variant="info" size="sm" className="me-sm-2 mb-2 mb-sm-0 text-white" onClick={() => handleViewResultsClick(interview)} disabled={loadingResults[interview.candidate_email]}>{loadingResults[interview.candidate_email] ? <Spinner size="sm"/> : "Results"}</Button>
//                                                         <Button variant="secondary" size="sm" onClick={() => handleViewResume(interview.candidate_email)} disabled={loadingResume[interview.candidate_email]}>{loadingResume[interview.candidate_email] ? <Spinner size="sm"/> : "Resume"}</Button>
//                                                     </div>
//                                                 </td>
//                                             </tr>
//                                         ))) : (<tr><td colSpan="7" className="text-center p-4">No interviews found.</td></tr>)}
//                                     </tbody>
//                                 </Table>
//                             </div>
//                             {totalPages > 1 && (<Pagination className="justify-content-center mt-4">{[...Array(totalPages).keys()].map(n=><Pagination.Item key={n+1} active={n+1===currentPage} onClick={()=>setCurrentPage(n+1)}>{n+1}</Pagination.Item>)}</Pagination>)}
//                         </div>
//                     </div>
//                 </div>
//             </div>
//             <ApplicantDetailsModal applicant={selectedApplicant || {}} show={showModal} onHide={() => setShowModal(false)} />
//             {showResultsModal && <ApplicantModal show={showResultsModal} onHide={() => setShowResultsModal(false)} applicant={selectedApplicantForResults} jobapplications={jobApplicationsForModal} setSuccessMessage={setSuccessMessage} updateJobApplication={updateJobApplicationStatusInList} />}
//         </>
//     );
// }

// export default InterviewerDashboard;

// import React, { useState, useEffect, useRef, useMemo } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import ApplicantDetailsModal from "../ProfileForm/ApplicantDetailsModal";
// import "../css/dash.css";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../css/PDFViewer.css";
// import { Link } from "react-router-dom";
// import { Pagination, Spinner, Button, Modal, Card, Table, Form, Alert, InputGroup } from "react-bootstrap";
// import axios from 'axios';
// import Chart from "chart.js/auto";

// // --- GLOBAL OPTIONS FOR MODAL ---
// // We define this here so the ApplicantModal below can access it.
// const jobStatusOptions = [
//     { value: "applied", label: "Applied", bgColor: "bg-info" },
//     { value: "under_review", label: "Under Review", bgColor: "bg-secondary" },
//     { value: "interview_scheduled", label: "Interview Scheduled", bgColor: "bg-warning text-dark" },
//     { value: "selected", label: "Selected", bgColor: "bg-success" },
//     { value: "rejected", label: "Rejected", bgColor: "bg-danger" },
// ];


// // --- APPLICANT MODAL COMPONENT (DEFINED IN-FILE) ---
// // By defining this component here, we don't need to create a separate file for it.
// const ApplicantModal = ({ show, onHide, applicant, jobapplications, setSuccessMessage, updateJobApplication }) => {
//     const chartRefs = useRef({});
//     const [personalityData, setPersonalityData] = useState([]);
//     const [personalityJobTitles, setPersonalityJobTitles] = useState([]);
//     const [allSubmittedAnswers, setAllSubmittedAnswers] = useState(null);
//     const [filteredSubmittedAnswers, setFilteredSubmittedAnswers] = useState(null);
//     const [currentJobId, setCurrentJobId] = useState(null);
//     const [jobQuestions, setJobQuestions] = useState(null);
//     const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);

//     useEffect(() => {
//         // Cleanup when modal closes or applicant changes
//         return () => {
//             Object.values(chartRefs.current).forEach(chart => chart?.destroy());
//             chartRefs.current = {};
//         };
//     }, [applicant]);

//     useEffect(() => {
//         if (show && applicant?.id) {
//             fetchPersonalityData(applicant.id);
//         }
//     }, [show, applicant]);
    
//     useEffect(() => {
//         // Redraw charts when data is available
//         Object.values(chartRefs.current).forEach(chart => chart?.destroy());
//         chartRefs.current = {};

//         if (Array.isArray(personalityData)) {
//             personalityData.forEach((data, index) => {
//                 const chartId = `personalityChart-${applicant?.id}-${index}`;
//                 const chartElement = document.getElementById(chartId);
//                 if (chartElement && data?.job_id) {
//                     const newChart = createDonutChart(data, chartId);
//                     if (newChart) chartRefs.current[index] = newChart;
//                 }
//             });
//         }
//     }, [personalityData, applicant?.id]);

//     useEffect(() => {
//         if (allSubmittedAnswers && currentJobId !== null) {
//             const filtered = allSubmittedAnswers.find(item => item.job_id === currentJobId);
//             setFilteredSubmittedAnswers(filtered || null);
//         } else {
//             setFilteredSubmittedAnswers(null);
//         }
//     }, [allSubmittedAnswers, currentJobId]);
    
//     const handleStatusChange = (id, newStatus) => {
//         axios.put(`https://raasbackend.vetrinahealthcare.com/applicants/update_status/${id}/`, { job_status: newStatus })
//             .then(() => {
//                 setSuccessMessage("Job status updated successfully");
//                 updateJobApplication(id, newStatus);
//                 setTimeout(() => setSuccessMessage(""), 5000);
//             }).catch(error => console.error("Error updating job status:", error));
//     };

//     const fetchPersonalityData = async (applicantId) => {
//         try {
//             const [personalityResponse, jobTitleResponse] = await Promise.all([
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`),
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantId}/`)
//             ]);
            
//             const fetchedPersonalityData = personalityResponse.data || [];
//             setPersonalityData(fetchedPersonalityData);

//             if (fetchedPersonalityData.length > 0) {
//                 const jobTitlesMap = (jobTitleResponse.data || []).reduce((acc, job) => {
//                     if (job.job_id) acc[job.job_id] = job.job_title;
//                     return acc;
//                 }, {});
//                 const jobTitles = fetchedPersonalityData.map(data => jobTitlesMap[data.job_id] || `Job ID: ${data.job_id}`);
//                 setPersonalityJobTitles(jobTitles);
//             }
//         } catch (error) {
//             console.error("Error fetching personality data:", error);
//         }
//     };
    
//     const handleShowSubmittedAnswers = async (applicantId, jobId) => {
//         setCurrentJobId(jobId);
//         setIsLoadingAnswers(true);
//         try {
//             const [answersResponse, questionResponse] = await Promise.all([
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/get_personality_results/${applicantId}/`),
//                 axios.get(`https://raasbackend.vetrinahealthcare.com/questions/${jobId}/`)
//             ]);
//             setAllSubmittedAnswers(answersResponse.data);
//             setJobQuestions(questionResponse.data || []);
//         } catch (error) {
//             console.error("Error fetching answers:", error);
//         } finally {
//             setIsLoadingAnswers(false);
//         }
//     };
    
//     const createDonutChart = (data, chartId) => {
//         const ctx = document.getElementById(chartId)?.getContext("2d");
//         if (!ctx || !data || typeof data.correct_count !== 'number' || typeof data.wrong_count !== 'number') return null;
        
//         return new Chart(ctx, {
//             type: 'doughnut',
//             data: {
//                 labels: [`Correct (${data.correct_count})`, `Wrong (${data.wrong_count})`],
//                 datasets: [{
//                     data: [data.correct_count, data.wrong_count],
//                     backgroundColor: ['rgba(40, 167, 69, 0.7)', 'rgba(220, 53, 69, 0.7)'],
//                 }]
//             },
//             options: { responsive: true, maintainAspectRatio: false, cutout: '60%', plugins: { legend: { display: false } } }
//         });
//     };

//     return (
//         <Modal show={show} onHide={onHide} dialogClassName="modal-xl" centered scrollable>
//             <Modal.Header closeButton><Modal.Title className="gradient-text">{applicant?.first_name || 'Applicant'} Details</Modal.Title></Modal.Header>
//             <Modal.Body className="p-4">
//                 <Card className="mb-4 shadow-sm"><Card.Header><h5 className="mb-0">Job Applications</h5></Card.Header>
//                     <Card.Body className="p-0">
//                         <Table striped bordered hover responsive size="sm" className="mb-0">
//                             <thead><tr><th>Job Title</th><th>Status</th><th>Applied On</th></tr></thead>
//                             <tbody>
//                                 {jobapplications.length > 0 ? jobapplications.map(jobApp => {
//                                     const statusOption = jobStatusOptions.find(opt => opt.value === jobApp.job_status);
//                                     return (
//                                         <tr key={jobApp.id}>
//                                             <td>{jobApp.job_title || 'N/A'}</td>
//                                             <td>
//                                                 <Form.Select size="sm" className={`${statusOption?.bgColor || ''}`} value={jobApp.job_status} onChange={(e) => handleStatusChange(jobApp.id, e.target.value)} style={{ color: statusOption?.bgColor.includes('text-dark') ? '#000' : '#fff' }}>
//                                                     {jobStatusOptions.map(option => <option key={option.value} value={option.value} style={{ backgroundColor: '#fff', color: '#000' }}>{option.label}</option>)}
//                                                 </Form.Select>
//                                             </td>
//                                             <td>{jobApp.applied_date ? new Date(jobApp.applied_date).toLocaleDateString() : 'N/A'}</td>
//                                         </tr>
//                                     );
//                                 }) : <tr><td colSpan="3" className="text-center p-3">No job applications found.</td></tr>}
//                             </tbody>
//                         </Table>
//                     </Card.Body>
//                 </Card>
//                 <h4 className="gradient-text text-center mt-4 mb-3">Personality Assessments</h4>
//                 <div className="d-flex flex-wrap justify-content-center gap-3">
//                     {personalityData.length > 0 ? personalityData.map((data, index) => (
//                         data && data.job_id ? (
//                             <Card key={`${data.job_id}-${index}`} className="text-center shadow-sm" style={{ minWidth: '260px', flex: '1 1 260px' }}>
//                                 <Card.Header><Card.Title as="h6" className="mb-0 text-truncate">{personalityJobTitles[index] || `Assessment ID: ${data.job_id}`}</Card.Title></Card.Header>
//                                 <Card.Body className="p-2">
//                                     <div style={{ height: '150px', width: '100%' }}><canvas id={`personalityChart-${applicant?.id}-${index}`}></canvas></div>
//                                     <p className="mb-1"><strong>Score:</strong> <b className="gradient-text">{typeof data.status === 'number' ? `${data.status.toFixed(1)}%` : 'N/A'}</b></p>
//                                     <Button variant="outline-info" size="sm" onClick={() => handleShowSubmittedAnswers(applicant.id, data.job_id)} disabled={isLoadingAnswers && currentJobId === data.job_id}>
//                                         {isLoadingAnswers && currentJobId === data.job_id ? <Spinner as="span" size="sm" /> : "View Answers"}
//                                     </Button>
//                                 </Card.Body>
//                             </Card>
//                         ) : null
//                     )) : <p className="text-center text-muted">No assessment data available.</p>}
//                 </div>
//                 {currentJobId && (
//                     <div className="mt-4"><Card className="shadow-sm"><Card.Header>
//                         <h5 className="mb-0">Submitted Answers for Job: {personalityJobTitles[personalityData.findIndex(p => p.job_id === currentJobId)] || `ID ${currentJobId}`}</h5>
//                     </Card.Header><Card.Body>
//                         {isLoadingAnswers ? <div className="text-center p-4"><Spinner /></div> : 
//                         filteredSubmittedAnswers && jobQuestions ? (
//                             <Table striped bordered responsive size="sm">
//                                 <thead><tr><th>#</th><th>Question</th><th>Your Answer</th><th>Result</th></tr></thead>
//                                 <tbody>
//                                     {jobQuestions.map((q, i) => {
//                                         const subAns = filteredSubmittedAnswers.selected_answers?.find(a => a.question_id === q.id);
//                                         const isCorrect = subAns && subAns.selected_answer_index === q.correct_answer_index;
//                                         return (
//                                             <tr key={q.id}>
//                                                 <td>{i + 1}</td><td>{q.question_text}</td>
//                                                 <td>{subAns ? (q.answers[subAns.selected_answer_index] || subAns.selected_answer_text) : <i>Not Answered</i>}</td>
//                                                 <td className="text-center">{subAns ? <span className={`badge ${isCorrect ? 'bg-success' : 'bg-danger'}`}>{isCorrect ? 'Correct' : 'Wrong'}</span> : <span className="badge bg-secondary">N/A</span>}</td>
//                                             </tr>
//                                         );
//                                     })}
//                                 </tbody>
//                             </Table>
//                         ) : <p className="text-center p-3">Could not load answers.</p>}
//                     </Card.Body></Card></div>
//                 )}
//             </Modal.Body>
//             <Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer>
//         </Modal>
//     );
// };


// // --- YOUR MAIN COMPONENT ---
// function InterviewerDashboard() {
//     const [interviewSchedule, setInterviewSchedule] = useState([]);
//     const [appliedJobs, setAppliedJobs] = useState({});
//     const [currentPage, setCurrentPage] = useState(1);
//     const itemsPerPage = 5;
//     const [searchQuery, setSearchQuery] = useState("");
//     const [loadingTable, setLoadingTable] = useState(true);
//     const [showModal, setShowModal] = useState(false);
//     const [selectedApplicant, setSelectedApplicant] = useState(null);
//     const [loadingView, setLoadingView] = useState({});
//     const [loadingResume, setLoadingResume] = useState({});

//     // State for the "Results" modal
//     const [showResultsModal, setShowResultsModal] = useState(false);
//     const [selectedApplicantForResults, setSelectedApplicantForResults] = useState(null);
//     const [jobApplicationsForModal, setJobApplicationsForModal] = useState([]);
//     const [loadingResults, setLoadingResults] = useState({});
//     const [successMessage, setSuccessMessage] = useState("");

//     useEffect(() => { fetchInterviewSchedule(); }, []);

//     const fetchInterviewSchedule = () => {
//         const userEmail = localStorage.getItem("email");
//         if (!userEmail) { setLoadingTable(false); return; }
//         setLoadingTable(true);
//         fetch(`https://raasbackend.vetrinahealthcare.com/interviewers/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: userEmail }) })
//             .then(res => res.json())
//             .then(data => {
//                 const formattedData = data.map(interview => ({ ...interview, date: formatDate(interview.date), time: formatTime(interview.time) }));
//                 setInterviewSchedule(formattedData);
//                 fetchAppliedJobsForSchedule(formattedData);
//             })
//             .catch(error => console.error("Error fetching schedule:", error))
//             .finally(() => setLoadingTable(false));
//     };

//     const fetchAppliedJobsForSchedule = (schedule) => {
//         schedule.forEach(interview => {
//             fetch(`https://raasbackend.vetrinahealthcare.com/get_applied_job/${interview.candidate_email}/`)
//                 .then(res => res.json())
//                 .then(data => setAppliedJobs(prev => ({ ...prev, [interview.candidate_email]: data.job_titles || ["N/A"] })))
//                 .catch(error => console.error("Error fetching applied jobs:", error));
//         });
//     };

//     const handleViewResultsClick = async (interview) => {
//         const email = interview.candidate_email;
//         if (!email) return;
//         setLoadingResults(prev => ({ ...prev, [email]: true }));
//         try {
//             const applicantRes = await axios.post("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { email });
//             if (!applicantRes.data || !applicantRes.data.id) throw new Error("Applicant ID not found.");
//             setSelectedApplicantForResults(applicantRes.data);
//             const jobsRes = await axios.get(`https://raasbackend.vetrinahealthcare.com/applicant/job-title/${applicantRes.data.id}/`);
//             setJobApplicationsForModal(jobsRes.data || []);
//             setShowResultsModal(true);
//         } catch (error) {
//             console.error("Error fetching data for results modal:", error);
//         } finally {
//             setLoadingResults(prev => ({ ...prev, [email]: false }));
//         }
//     };
    
//     const updateJobApplicationStatusInList = (jobAppId, newStatus) => {
//         setJobApplicationsForModal(prev => prev.map(app => app.id === jobAppId ? { ...app, job_status: newStatus } : app));
//     };

//     const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-GB');
//     const formatTime = (timeString) => new Date(`01/01/2022 ${timeString}`).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

//     const handleViewClick = (email) => {
//         setLoadingView({ ...loadingView, [email]: true });
//         fetch("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) })
//             .then(res => res.json()).then(data => { setSelectedApplicant(data); setShowModal(true); })
//             .catch(err => console.error("Error fetching applicant data:", err))
//             .finally(() => setLoadingView({ ...loadingView, [email]: false }));
//     };

//     const handleViewResume = (email) => {
//         setLoadingResume({ ...loadingResume, [email]: true });
//         fetch("https://raasbackend.vetrinahealthcare.com/view_resume/", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) })
//             .then(res => res.blob()).then(blob => window.open(URL.createObjectURL(blob), "_blank"))
//             .catch(err => console.error("Error fetching resume:", err))
//             .finally(() => setLoadingResume({ ...loadingResume, [email]: false }));
//     };

//     const filteredSchedule = interviewSchedule.filter(i => Object.values(i).some(val => String(val).toLowerCase().includes(searchQuery.toLowerCase())) || appliedJobs[i.candidate_email]?.some(j => j.toLowerCase().includes(searchQuery.toLowerCase())));
//     const currentItems = filteredSchedule.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
//     const totalPages = Math.ceil(filteredSchedule.length / itemsPerPage);

//     return (
//         <>
//             <DashNavbar />
//             <div className="wrapper">
//                 <Sidebar />
//                 <div className="col-lg-10 col-md-8 col-sm-12 main dash4">
//                     <div className="container mt-4">
//                         <div className="text-start"><h3>Welcome to your Dashboard</h3><p>Below is your interview schedule:</p></div>
//                         <div className="auth-inner mt-5 w-100" style={{ padding: "10px" }}>
//                             <h2 className="mb-4">Interview Schedule</h2>
//                             <div className="row mb-3"><div className="col-12 col-md-6 mx-auto"><InputGroup><Form.Control type="search" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} /></InputGroup></div></div>
//                             <div className="table-responsive">
//                                 <Table striped bordered hover size="sm">
//                                     <thead className="thead-dark"><tr><th>Sr.No</th><th>Candidate</th><th>Date</th><th>Time</th><th>Applied Job</th><th>Meeting</th><th>Actions</th></tr></thead>
//                                     <tbody>
//                                         {loadingTable ? (<tr><td colSpan="7" className="text-center p-4"><Spinner /></td></tr>)
//                                         : currentItems.length > 0 ? (currentItems.map((interview, index) => (
//                                             <tr key={interview.id || index}>
//                                                 <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
//                                                 <td>{interview.candidate}</td>
//                                                 <td>{interview.date}</td>
//                                                 <td>{interview.time}</td>
//                                                 <td>{appliedJobs[interview.candidate_email]?.join(", ")}</td>
//                                                 <td><Link to={interview.meetLink} target="_blank" className="btn btn-primary btn-sm">Join</Link></td>
//                                                 <td>
//                                                     <div className="d-flex flex-sm-row flex-column">
//                                                         <Button variant="secondary" size="sm" className="me-sm-2 mb-2 mb-sm-0" onClick={() => handleViewClick(interview.candidate_email)} disabled={loadingView[interview.candidate_email]}>{loadingView[interview.candidate_email] ? <Spinner size="sm"/> : "View"}</Button>
//                                                         <Button variant="info" size="sm" className="me-sm-2 mb-2 mb-sm-0 text-white" onClick={() => handleViewResultsClick(interview)} disabled={loadingResults[interview.candidate_email]}>{loadingResults[interview.candidate_email] ? <Spinner size="sm"/> : "Results"}</Button>
//                                                         <Button variant="secondary" size="sm" onClick={() => handleViewResume(interview.candidate_email)} disabled={loadingResume[interview.candidate_email]}>{loadingResume[interview.candidate_email] ? <Spinner size="sm"/> : "Resume"}</Button>
//                                                     </div>
//                                                 </td>
//                                             </tr>
//                                         ))) : (<tr><td colSpan="7" className="text-center p-4">No interviews found.</td></tr>)}
//                                     </tbody>
//                                 </Table>
//                             </div>
//                             {totalPages > 1 && (<Pagination className="justify-content-center mt-4">{[...Array(totalPages).keys()].map(n=><Pagination.Item key={n+1} active={n+1===currentPage} onClick={()=>setCurrentPage(n+1)}>{n+1}</Pagination.Item>)}</Pagination>)}
//                         </div>
//                     </div>
//                 </div>
//             </div>
//             <ApplicantDetailsModal applicant={selectedApplicant || {}} show={showModal} onHide={() => setShowModal(false)} />
//             {showResultsModal && <ApplicantModal show={showResultsModal} onHide={() => setShowResultsModal(false)} applicant={selectedApplicantForResults} jobapplications={jobApplicationsForModal} setSuccessMessage={setSuccessMessage} updateJobApplication={updateJobApplicationStatusInList} />}
//         </>
//     );
// }

// export default InterviewerDashboard;

// import React, { useState, useEffect } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import ApplicantDetailsModal from "../ProfileForm/ApplicantDetailsModal";
// import "../css/dash.css";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../css/PDFViewer.css";
// import { Link } from "react-router-dom";
// import { Pagination } from "react-bootstrap";
// import { Spinner } from "react-bootstrap"; // <-- Added Spinner from react-bootstrap

// function InterviewerDashboard() {
//   const [interviewSchedule, setInterviewSchedule] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [selectedApplicant, setSelectedApplicant] = useState(null);
//   const [appliedJobs, setAppliedJobs] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 5;
//   const [searchQuery, setSearchQuery] = useState("");
//   const [loadingTable, setLoadingTable] = useState(true); // <-- New state for table loader
//   const [loadingView, setLoadingView] = useState(""); // <-- New state for View button loader
//   const [loadingResume, setLoadingResume] = useState({});

//   useEffect(() => {
//     fetchInterviewSchedule();
//   }, []);

//   const fetchInterviewSchedule = () => {
//     const userEmail = localStorage.getItem("email");
//     if (!userEmail) {
//       console.error("User email not found in local storage");
//       return;
//     }
//     setLoadingTable(true); // <-- Enable loader before data fetch

//     fetch(`https://raasbackend.vetrinahealthcare.com/interviewers/`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: userEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.json();
//       })
//       .then((data) => {
//         const formattedData = data.map((interview) => ({
//           ...interview,
//           date: formatDate(interview.date),
//           time: formatTime(interview.time),
//         }));
//         setInterviewSchedule(formattedData);

//         // Fetch applied jobs for the interview schedule
//         fetchAppliedJobsForSchedule(formattedData);
//       })
//       .catch((error) => {
//         console.error("Error fetching interview schedule:", error);
//       })
//       .finally(() => setLoadingTable(false)); // <-- Disable loader after data fetch
//   };

//   const fetchAppliedJobsForSchedule = (schedule) => {
//     const candidateEmails = schedule.map(
//       (interview) => interview.candidate_email
//     );
//     candidateEmails.forEach((email) => {
//       fetch(`https://raasbackend.vetrinahealthcare.com/get_applied_job/${email}/`)
//         .then((response) => {
//           if (!response.ok) {
//             throw new Error("Network response was not ok");
//           }
//           return response.json();
//         })
//         .then((appliedJobsResponse) => {
//           setAppliedJobs((prevJobs) => ({
//             ...prevJobs,
//             [email]: appliedJobsResponse.job_titles || ["Not Applied"],
//           }));
//         })
//         .catch((error) => {
//           console.error("Error fetching applied jobs:", error);
//         });
//     });
//   };

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     const formattedDate = `${date.getDate()}-${date.getMonth() + 1
//       }-${date.getFullYear()}`;
//     return formattedDate;
//   };

//   const formatTime = (timeString) => {
//     const time = new Date(`01/01/2022 ${timeString}`);
//     const formattedTime = time.toLocaleTimeString("en-US", {
//       hour: "numeric",
//       minute: "2-digit",
//     });
//     return formattedTime;
//   };

//   const handleViewClick = (candidateEmail) => {
//     setLoadingView((prev) => ({ ...prev, [candidateEmail]: true })); // Start loader for specific button
//     fetch("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: candidateEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.json();
//       })
//       .then((data) => {
//         setSelectedApplicant(data);
//         setShowModal(true);
//       })
//       .catch((error) => {
//         console.error("Error fetching applicant data:", error);
//       })
//       .finally(() => {
//         setLoadingView((prev) => ({ ...prev, [candidateEmail]: false })); // Stop loader for specific button
//       });

//   };

//   const handleViewResume = (candidateEmail) => {
//     setLoadingResume((prev) => ({ ...prev, [candidateEmail]: true })); // Start loader for specific button
//     fetch("https://raasbackend.vetrinahealthcare.com/view_resume/", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: candidateEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.blob();
//       })
//       .then((blob) => {
//         const resumeBlobUrl = URL.createObjectURL(blob);
//         window.open(resumeBlobUrl, "_blank");
//       })
//       .catch((error) => {
//         console.error("Error fetching resume:", error);
//       })
//       .finally(() => {
//         setLoadingResume((prev) => ({ ...prev, [candidateEmail]: false })); // Stop loader for specific button
//       });
//   };

//   // Pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = interviewSchedule.slice(
//     indexOfFirstItem,
//     indexOfLastItem
//   );

//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   const totalPages = Math.ceil(interviewSchedule.length / itemsPerPage);
//   const handleSearch = (e) => {
//     setSearchQuery(e.target.value);
//   };

//   const filteredSchedule = interviewSchedule.filter((interview) => {
//     const candidateMatch = interview.candidate
//       .toLowerCase()
//       .includes(searchQuery.toLowerCase());
//     const dateMatch = interview.date
//       .toLowerCase()
//       .includes(searchQuery.toLowerCase());
//     const appliedJobMatch = appliedJobs[interview.candidate_email]?.some(
//       (job) => job.toLowerCase().includes(searchQuery.toLowerCase())
//     );
//     return candidateMatch || dateMatch || appliedJobMatch;
//   });

//   return (
//     <>
//       {/* <div
//         style={{ display: "flex", flexDirection: "column", height: "100vh" }}
//       > */}
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="col-lg-10 col-md-8 col-sm-12 main dash4">
//           <div className="container mt-4">
//             <div className="text-start">
//               <h3>Welcome to your Dashboard</h3>
//               <p>Below is your interview schedule:</p>
//             </div>
//             <div className="auth-inner mt-5 w-100">
//               <h2 className="mb-4">Interview Schedule</h2>
//               <div className="input-group justify-content-center align-items-center">
//                 <div className="form-outline w-50">
//                   <input
//                     type="search"
//                     className="form-control"
//                     placeholder="Search by Name or Email"
//                     onChange={handleSearch}
//                   />
//                   <button className="btn btn-primary searchicon text-xs">
//                     <i className="fas fa-search"></i>
//                   </button>
//                 </div>
//               </div>
//               <div className="table-responsive">
//                 <table className="table">
//                   <thead className="thead-dark">
//                     <tr>
//                       <th scope="col">Sr.No</th>
//                       <th scope="col">Candidate</th>
//                       <th scope="col">Date</th>
//                       <th scope="col">Time</th>
//                       <th scope="col">Applied Job</th>
//                       <th scope="col">Meeting Link</th>
//                       <th scope="col">Actions</th>
//                     </tr>
//                   </thead>
//                   {loadingTable ? ( // <-- Conditional rendering for loader
//                     <tbody>
//                       <tr>
//                         <td colSpan="7" className="text-center">
//                           <Spinner animation="border" /> {/* <-- Spinner */}
//                         </td>
//                       </tr>
//                     </tbody>
//                   ) : (
//                     <tbody>
//                       {filteredSchedule
//                         .slice(indexOfFirstItem, indexOfLastItem)
//                         .map((interview, index) => (
//                           <tr key={index}>
//                             <td>{indexOfFirstItem + index + 1}</td>
//                             <td>{interview.candidate}</td>
//                             <td>{interview.date}</td>
//                             <td>{interview.time}</td>
//                             <td className="w-25">
//                               {appliedJobs[interview.candidate_email]?.join(", ")}
//                             </td>
//                             <td>
//                               <Link
//                                 to={interview.meetLink}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 className="btn btn-primary"
//                               >
//                                 Join Meeting
//                               </Link>
//                             </td>
//                             <td>
//                               <button
//                                 className="btn btn-secondary me-2 mb-8 mb-sm-0"
//                                 onClick={() =>
//                                   handleViewClick(interview.candidate_email)
//                                 }

//                                 disabled={loadingView[interview.candidate_email]} // Disable button based on specific email

//                               >

//                                 {loadingView[interview.candidate_email] ? ( // Show spinner only for the loading button
//                                   <span
//                                     className="spinner-border spinner-border-sm"
//                                     role="status"
//                                     aria-hidden="true"
//                                   ></span>
//                                 ) : (
//                                   "View"
//                                 )}

//                               </button>
//                               <button
//                                 className="btn btn-secondary mb-8 mb-sm-0"
//                                 onClick={() =>
//                                   handleViewResume(interview.candidate_email)
//                                 }
//                                 disabled={loadingResume[interview.candidate_email]} // Disable button based on specific email
//                               >
//                                 {loadingResume[interview.candidate_email] ? ( // Show spinner only for the loading button
//                                   <span
//                                     className="spinner-border spinner-border-sm"
//                                     role="status"
//                                     aria-hidden="true"
//                                   ></span>
//                                 ) : (
//                                   "Resume"
//                                 )}

//                               </button>
//                             </td>
//                           </tr>
//                         ))}
//                     </tbody>
//                   )}
//                 </table>
//               </div>
//               <Pagination className="mt-4">
//                 {/* <Pagination.First onClick={() => paginate(1)} disabled={currentPage === 1} /> */}
//                 <Pagination.Prev
//                   onClick={() => paginate(currentPage - 1)}
//                   disabled={currentPage === 1}
//                 />
//                 {Array.from({ length: totalPages }, (_, i) => (
//                   <Pagination.Item
//                     key={i + 1}
//                     active={i + 1 === currentPage}
//                     onClick={() => paginate(i + 1)}
//                   >
//                     {i + 1}
//                   </Pagination.Item>
//                 ))}
//                 <Pagination.Next
//                   onClick={() => paginate(currentPage + 1)}
//                   disabled={currentPage === totalPages}
//                 />
//                 {/* <Pagination.Last onClick={() => paginate(totalPages)} disabled={currentPage === totalPages} /> */}
//               </Pagination>
//             </div>
//           </div>
//         </div>
//       </div>
//       {/* </div> */}

//       <ApplicantDetailsModal
//         applicant={selectedApplicant || {}}
//         show={showModal}
//         onHide={() => setShowModal(false)}
//       />
//     </>
//   );
// }

// export default InterviewerDashboard;

//old code

// import React, { useState, useEffect } from "react";
// import DashNavbar from "./DashNavbar";
// import Sidebar from "./Sidebar";
// import ApplicantDetailsModal from "../ProfileForm/ApplicantDetailsModal";
// import "../css/dash.css";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../css/PDFViewer.css";
// import { Link } from "react-router-dom";
// import { Pagination } from "react-bootstrap";
// import { Spinner } from "react-bootstrap"; // <-- Added Spinner from react-bootstrap

// function InterviewerDashboard() {
//   const [interviewSchedule, setInterviewSchedule] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [selectedApplicant, setSelectedApplicant] = useState(null);
//   const [appliedJobs, setAppliedJobs] = useState({});
//   const [currentPage, setCurrentPage] = useState(1);
//   const itemsPerPage = 5;
//   const [searchQuery, setSearchQuery] = useState("");
//   const [loadingTable, setLoadingTable] = useState(true); // <-- New state for table loader
//   const [loadingView, setLoadingView] = useState(""); // <-- New state for View button loader
//   const [loadingResume, setLoadingResume] = useState({});

//   useEffect(() => {
//     fetchInterviewSchedule();
//   }, []);

//   const fetchInterviewSchedule = () => {
//     const userEmail = localStorage.getItem("email");
//     if (!userEmail) {
//       console.error("User email not found in local storage");
//       return;
//     }
//     setLoadingTable(true); // <-- Enable loader before data fetch

//     fetch(`https://raasbackend.vetrinahealthcare.com/interviewers/`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: userEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.json();
//       })
//       .then((data) => {
//         const formattedData = data.map((interview) => ({
//           ...interview,
//           date: formatDate(interview.date),
//           time: formatTime(interview.time),
//         }));
//         setInterviewSchedule(formattedData);

//         // Check if no interviews are scheduled and set a flag
//         if (formattedData.length === 0) {
//           setInterviewSchedule(null); // <-- Set interview schedule as null if empty
//         } else {
//           // Fetch applied jobs for the interview schedule
//           fetchAppliedJobsForSchedule(formattedData);
//         }
//       })
//       .catch((error) => {
//         console.error("Error fetching interview schedule:", error);
//       })
//       .finally(() => setLoadingTable(false)); // <-- Disable loader after data fetch
//   };

//   const fetchAppliedJobsForSchedule = (schedule) => {
//     const candidateEmails = schedule.map(
//       (interview) => interview.candidate_email
//     );
//     candidateEmails.forEach((email) => {
//       fetch(`https://raasbackend.vetrinahealthcare.com/get_applied_job/${email}/`)
//         .then((response) => {
//           if (!response.ok) {
//             throw new Error("Network response was not ok");
//           }
//           return response.json();
//         })
//         .then((appliedJobsResponse) => {
//           setAppliedJobs((prevJobs) => ({
//             ...prevJobs,
//             [email]: appliedJobsResponse.job_titles || ["Not Applied"],
//           }));
//         })
//         .catch((error) => {
//           console.error("Error fetching applied jobs:", error);
//         });
//     });
//   };

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     const formattedDate = `${date.getDate()}-${date.getMonth() + 1
//       }-${date.getFullYear()}`;
//     return formattedDate;
//   };

//   const formatTime = (timeString) => {
//     const time = new Date(`01/01/2022 ${timeString}`);
//     const formattedTime = time.toLocaleTimeString("en-US", {
//       hour: "numeric",
//       minute: "2-digit",
//     });
//     return formattedTime;
//   };

//   const handleViewClick = (candidateEmail) => {
//     setLoadingView((prev) => ({ ...prev, [candidateEmail]: true })); // Start loader for specific button
//     fetch("https://raasbackend.vetrinahealthcare.com/get_applicant_data/", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: candidateEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.json();
//       })
//       .then((data) => {
//         setSelectedApplicant(data);
//         setShowModal(true);
//       })
//       .catch((error) => {
//         console.error("Error fetching applicant data:", error);
//       })
//       .finally(() => {
//         setLoadingView((prev) => ({ ...prev, [candidateEmail]: false })); // Stop loader for specific button
//       });

//   };

//   const handleViewResume = (candidateEmail) => {
//     setLoadingResume((prev) => ({ ...prev, [candidateEmail]: true })); // Start loader for specific button
//     fetch("https://raasbackend.vetrinahealthcare.com/view_resume/", {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({ email: candidateEmail }),
//     })
//       .then((response) => {
//         if (!response.ok) {
//           throw new Error("Network response was not ok");
//         }
//         return response.blob();
//       })
//       .then((blob) => {
//         const resumeBlobUrl = URL.createObjectURL(blob);
//         window.open(resumeBlobUrl, "_blank");
//       })
//       .catch((error) => {
//         console.error("Error fetching resume:", error);
//       })
//       .finally(() => {
//         setLoadingResume((prev) => ({ ...prev, [candidateEmail]: false })); // Stop loader for specific button
//       });
//   };

//   // Pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = interviewSchedule ? interviewSchedule.slice(
//     indexOfFirstItem,
//     indexOfLastItem
//   ) : [];

//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   const totalPages = interviewSchedule ? Math.ceil(interviewSchedule.length / itemsPerPage) : 1;

//   const handleSearch = (e) => {
//     setSearchQuery(e.target.value);
//   };

//   const filteredSchedule = interviewSchedule ? interviewSchedule.filter((interview) => {
//     const candidateMatch = interview.candidate
//       .toLowerCase()
//       .includes(searchQuery.toLowerCase());
//     const dateMatch = interview.date
//       .toLowerCase()
//       .includes(searchQuery.toLowerCase());
//     const appliedJobMatch = appliedJobs[interview.candidate_email]?.some(
//       (job) => job.toLowerCase().includes(searchQuery.toLowerCase())
//     );
//     return candidateMatch || dateMatch || appliedJobMatch;
//   }) : [];

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="col-lg-10 col-md-8 col-sm-12 main dash4">
//           <div className="container mt-4">
//             <div className="text-start">
//               <h3>Welcome to your Dashboard</h3>
//               <p>Below is your interview schedule:</p>
//             </div>
//             <div className="auth-inner mt-5 w-100">
//               <h2 className="mb-4">Interview Schedule</h2>
//               <div className="input-group justify-content-center align-items-center">
//                 <div className="form-outline w-50">
//                   <input
//                     type="search"
//                     className="form-control"
//                     placeholder="Search by Name or Email"
//                     onChange={handleSearch}
//                   />
//                   <button className="btn btn-primary searchicon text-xs">
//                     <i className="fas fa-search"></i>
//                   </button>
//                 </div>
//               </div>
//               <div className="table-responsive">
//                 <table className="table">
//                   <thead className="thead-dark">
//                     <tr>
//                       <th scope="col">Sr.No</th>
//                       <th scope="col">Candidate</th>
//                       <th scope="col">Date</th>
//                       <th scope="col">Time</th>
//                       <th scope="col">Applied Job</th>
//                       <th scope="col">Meeting Link</th>
//                       <th scope="col">Actions</th>
//                     </tr>
//                   </thead>
//                   {loadingTable ? (
//                     <tbody>
//                       <tr>
//                         <td colSpan="7" className="text-center">
//                           <Spinner animation="border" />
//                         </td>
//                       </tr>
//                     </tbody>
//                   ) : interviewSchedule === null ? (
//                     <tbody>
//                       <tr>
//                         <td colSpan="7" className="text-center">
//                           Your interview has not been scheduled with any candidate.
//                         </td>
//                       </tr>
//                     </tbody>
//                   ) : (
//                     <tbody>
//                       {filteredSchedule
//                         .slice(indexOfFirstItem, indexOfLastItem)
//                         .map((interview, index) => (
//                           <tr key={index}>
//                             <td>{indexOfFirstItem + index + 1}</td>
//                             <td>{interview.candidate}</td>
//                             <td>{interview.date}</td>
//                             <td>{interview.time}</td>
//                             <td className="w-25">
//                               {appliedJobs[interview.candidate_email]?.join(", ")}
//                             </td>
//                             <td>
//                               <Link
//                                 to={interview.meetLink}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 className="btn btn-primary"
//                               >
//                                 Join Meeting
//                               </Link>
//                             </td>
//                             <td>
//                               <button
//                                 className="btn btn-secondary me-2 mb-8 mb-sm-0"
//                                 onClick={() =>
//                                   handleViewClick(interview.candidate_email)
//                                 }
//                                 disabled={loadingView[interview.candidate_email]}
//                               >
//                                 {loadingView[interview.candidate_email] ? (
//                                   <span
//                                     className="spinner-border spinner-border-sm"
//                                     role="status"
//                                     aria-hidden="true"
//                                   ></span>
//                                 ) : (
//                                   "View"
//                                 )}
//                               </button>
//                               <button
//                                 className="btn btn-secondary mb-8 mb-sm-0"
//                                 onClick={() =>
//                                   handleViewResume(interview.candidate_email)
//                                 }
//                                 disabled={loadingResume[interview.candidate_email]}
//                               >
//                                 {loadingResume[interview.candidate_email] ? (
//                                   <span
//                                     className="spinner-border spinner-border-sm"
//                                     role="status"
//                                     aria-hidden="true"
//                                   ></span>
//                                 ) : (
//                                   "Resume"
//                                 )}
//                               </button>
//                             </td>
//                           </tr>
//                         ))}
//                     </tbody>
//                   )}
//                 </table>
//               </div>
//               <Pagination className="mt-4">
//                 <Pagination.Prev
//                   onClick={() => paginate(currentPage - 1)}
//                   disabled={currentPage === 1}
//                 />
//                 {Array.from({ length: totalPages }, (_, i) => (
//                   <Pagination.Item
//                     key={i + 1}
//                     active={i + 1 === currentPage}
//                     onClick={() => paginate(i + 1)}
//                   >
//                     {i + 1}
//                   </Pagination.Item>
//                 ))}
//                 <Pagination.Next
//                   onClick={() => paginate(currentPage + 1)}
//                   disabled={currentPage === totalPages}
//                 />
//               </Pagination>
//             </div>
//           </div>
//         </div>
//       </div>

//       <ApplicantDetailsModal
//         applicant={selectedApplicant || {}}
//         show={showModal}
//         onHide={() => setShowModal(false)}
//       />
//     </>
//   );
// }

// export default InterviewerDashboard;
